/* =====================================================================
   mk-portal-delivery.js  (Shopify theme asset)  -  window.MKPortalDelivery
   MK Supplements customer portal (Skio Customer Portal v3, /a/account/*)

   Two cards built from the delivery-status service and Skio's GraphQL:
   1. "LATEST ORDER" timeline on /a/account/subscriptions/<id>: status
      headline, Ordered / Shipped / Delivered-or-arrival / Next order steps,
      and a "Show delivery schedule" panel (Coming up + Delivered).
   2. Delivery banner on /a/account/subscriptions ("Your Oct 9 order is on
      its way"), rendered into the home module's
      <div id="mk-delivery-banner-slot"> when it exists, otherwise right
      under Skio's greeting.
   3. Orders page (/a/account/orders): one status line ("On its way:
      arriving Friday, Oct 16 . Track package") under the header of each of
      Skio's order cards whose "Order #NNNN" matches an order from the
      delivery service, so the page that "View order" / "View order
      history" link to says the same as the banner and the timeline. Cards
      that do not match (older than 60 days, other numbering) get nothing.

   Needs mk-portal-core.js (window.MKPortal) loaded first and the gate class
   html.mk-portal-on. Read-only: no Skio mutations, no navigation by script;
   links are plain links (full page loads).

   FAIL CLOSED: any error, missing data or unexpected page shape = the card
   is not shown (or removed) and Skio's own page stays exactly as it is.
   Nothing native is hidden or moved by this module.

   COPY RULES enforced here: "arriving <date>" only from the carrier/Shopify
   estimate (estimatedDeliveryAt), "Arrives about <date>" only from this
   customer's own delivered orders, streak counts only when they equal
   Skio's own streak badge, tier milestones only when every check passes.
   No payment details are shown.
   Carrier names (batch 2, 2026-10-11): the service's own "carrier" field
   (version 2.2: from the tracking company or the newest scans, one name per
   carrier) whenever the answer has it, null included (then no name). For a
   version 2.1 answer (no such field) only a real parcel carrier from
   CARRIERS is named (CR-2): Shopify's tracking company on MK's orders is
   the warehouse ("ShipBob"), which is never shown. Worded "Shipped with
   USPS" (never "Delivered by": a hand-off service such as UPS SurePost is
   often delivered by USPS). Without a name the phrase is left out with its
   separator.
   TRACK PACKAGE (batch 3, approved by Jim 2026-10-12): ONE action. The
   home banner's "Track package" button (two or more packages: "Track
   packages"), the Order history line's "Track package", the timeline's
   "Track package" under its steps and the "Track package" of a delivered
   order in the timeline's schedule open the same pop-up (MKPortal.dialog:
   a centred dialog on desktop / tablet, a bottom sheet on phones) for that
   order: "Order #28761 . 2 items", the status headline, "Shipped with
   USPS", a 4-step progress strip, then the scan history (newest first,
   grouped by day: what happened in plain words, the time in the customer's
   time zone, city / state when given) drawn at once from the 10 newest
   scans /v1/orders already gave. "Show all" only when a package shows the
   full 10 (there may be more): that click, and only that click, asks
   MKPortal.tracking('#28761') (GET /v1/tracking);
   loading, error (Try again: {fresh: true}), "not available" (404) and
   the moreScans / moreShipments notes. Two to five packages: one tab
   each. At the foot one small link "View on the tracking site" (the
   package's https tracking URL, new tab; r6: not "the carrier's", since
   a ShipBob number opens ShipBob's own page). The separate "Tracking
   updates" link and the #mk-tracking landing are gone.
   SCAN NOTES (r6, delivery service 2.3): each scan may carry "note", the
   carrier's own words already cleaned by the service (names, addresses,
   numbers and the warehouse removed; null when nothing safe was left).
   The note is the row's main line, the status words stay as a small tag
   only when the note does not already say them, city / state on the line
   below; text nodes only. The note is cleaned again here (cleanNote) and
   never feeds the carrier name. A 2.2 answer (no note) draws exactly the
   rows of 1.1.1. One tracking verb (C7): the timeline and Order history
   say "Delayed" / "Delivery attempted" (no second tracking hint).
   Option B (Jim, 2026-10-12): the subscription page's timeline card has
   no list of its own; its "Track package" (shown when the banner's would
   be) opens the pop-up for the order it shows, so the scans are in one
   place. The Shipped step names the carrier only (no external link).
   Service 2.1 answers (no events): the pop-up shows the status and the
   carrier link only.
   RETRY AND KEEP (r7, 1.2.1, Cowork's E1: no "Latest order" card at
   606 px on the test theme, not a width bug): a read that fails (the
   service's 429 / 5xx / timeout, or one of Skio's queries) is tried again
   after RETRY_MS (4 s), or RETRY_429_MS (60 s) after a 429, at most
   MAX_RETRIES (2) times per route, only while the route is the same and
   the tab is visible (config.deliveryRetryMs, 200..120000, sets both
   waits: tests). A fresh re-read after Skio's header changed (a skip, a
   new date, products) whose delivery read fails keeps the orders already
   shown, but Skio's subscription is still read fresh and the card is
   drawn again from it (Next order, Coming up), and the delivery read is
   tried again the same way. If Skio's read fails too, the card is
   removed (as in r6: no card rather than an out-of-date one). Each
   header change gets its own retries. Only a real answer ("no orders",
   no matching order) removes the card otherwise.
   MKPortalDelivery.state() says why a card is absent (codes only).
   ===================================================================== */
(function () {
  'use strict';
  var M = window.MKPortal;
  if (!M || !M.__core || typeof M.on !== 'function' || !M.on()) return;
  if (window.MKPortalDelivery) return;
  if (!M.schedule) return;                       /* the shared schedule rules (core, round 2) */
  var CONFIG = M.config || {};
  if (CONFIG.modules && CONFIG.modules.delivery === false) return;

  /* ------------------------------------------------------------------ */
  /* settings                                                             */
  /* ------------------------------------------------------------------ */
  var VERSION = '1.2.1';          /* 1.0.1 (2026-10-11): carrier allowlist, no warehouse name (CR-2); 1.0.2: service carrier field,
                                     "Shipped with", tracking updates (batch 2, with the review fixes of 2026-10-11: no carrier
                                     before an order ships, scan times as real instants, per-order "Show all" state, one
                                     persistent status line, per-package "newest 100" note, fallback names = the service's);
                                     1.1.0 (2026-10-12, batch 3): ONE "Track package" action (the pop-up), "Tracking updates"
                                     link and #mk-tracking landing removed, timeline list "Scan history" + carrier link,
                                     /v1/tracking packages matched by tracking number (MKPortal.track.merge);
                                     1.1.1 (2026-10-12, option B): the timeline's "Track package" opens the same pop-up; its
                                     inline list ("Hide tracking", "Scan history", "Show all", the small carrier link) removed;
                                     1.2.0 (2026-10-13, r6): scan notes from the service 2.3 (C1: the note as the row's main line,
                                     a status tag only when it adds something, cleanNote, trackState().noteRows); one tracking verb
                                     (C7: "Delayed" / "Delivery attempted", "View on the tracking site"); r6 review fixes
                                     (2026-10-13): a state code that is also a scan word ("in", "or", "id") is upper-cased
                                     only at the end of a note; cleanNote drops split phone / tracking numbers;
                                     1.2.1 (2026-10-15, r7, E1): a failed read is tried again (4 s, 60 s after a 429, at most
                                     twice per route, tab visible), a failed fresh re-read keeps the card already shown,
                                     state() gives the reason a card is absent (codes only); r7 review fix (2026-10-15): a
                                     failed delivery read on a re-read keeps the orders only, Skio's subscription is read
                                     fresh (no old Next order date / Coming up list), a failed Skio read removes the card,
                                     every header change gets its own retries */
  /* the pop-up needs the core's dialog and track helpers (core 1.1.0); without them no Track package button is drawn */
  var POP = !!(M.dialog && typeof M.dialog.open === 'function' && M.track && typeof M.track.merge === 'function' &&
    typeof M.track.url === 'function' && typeof M.tracking === 'function');
  var SITE_ID = '55c10ac1-455d-48a6-a41e-161371d88114';   /* MK's Skio site (verified live) */
  var DETAIL_LIMIT = 5;            /* orders read on a subscription page (service max) */
  var HOME_LIMIT = 3;              /* orders read for the home banner */
  var UPCOMING_COUNT = 6;          /* "Coming up" rows, the next order included */
  var ARRIVAL_MIN_SAMPLES = 1;     /* delivered orders needed before "Arrives about <date>" is shown */
  var USUAL_MIN_SAMPLES = 2;       /* shipments needed before "Usually arrives N days after it ships" */
  var ARRIVAL_MAX_DAYS = 30;       /* ignore history samples longer than this (returns, re-ships) */
  var BANNER_DELIVERED_MS = 2 * 86400000;   /* banner stays this long after delivery */
  var BANNER_PREPARING_DAYS = 7;   /* processing / label created: banner only this many days */
  var SHOW_STREAK = true;          /* "Would be N in a row": only when Skio's own badge shows the same count */
  var SHOW_TIER_MILESTONES = true; /* "Unlocks <tier>" pill: only single-subscription customers, all checks pass */
  var CONTACT_FALLBACK = '/pages/contact-us';   /* the theme's contact page (seen in the live footer) */
  var ORDERS_PATH = '/a/account/orders';         /* no per-order page exists in the portal */
  var ORDERS_LIMIT = 5;            /* orders read for the orders page (service max, 60 days) */
  /* r7 (E1): a failed read is tried again after RETRY_MS (RETRY_429_MS after the service's 429, its own
     "retry after"; 20 reads a minute per customer), at most MAX_RETRIES times per route. Tests set both waits
     with config.deliveryRetryMs (clamped to 200..120000) */
  var RETRY_MS = 4000;
  var RETRY_429_MS = 60000;
  var MAX_RETRIES = 2;
  if (typeof CONFIG.deliveryRetryMs === 'number' && isFinite(CONFIG.deliveryRetryMs)) {
    RETRY_MS = RETRY_429_MS = Math.max(200, Math.min(120000, Math.round(CONFIG.deliveryRetryMs)));
  }
  /* parcel carriers that may be named (CR-2). Key: the tracking company in lower case with every run of
     other characters turned into one space; value: the name shown. Exact matches only (a few service-level
     spellings such as "UPS Ground" are listed one by one; never a prefix match, which could name the wrong
     company): anything else (ShipBob or another warehouse / 3PL, an unknown or a garbled name) is not shown.
     Only used for version 2.1 answers; the values are the delivery service 2.2's own names (worker.js
     CARRIERS, HANDOFF Appendix A: one name per carrier), so a shipment keeps its name across the deploy. */
  var CARRIERS = {
    'usps': 'USPS', 'u s p s': 'USPS', 'united states postal service': 'USPS', 'us postal service': 'USPS', 'u s postal service': 'USPS',
    'usps ground advantage': 'USPS', 'usps priority mail': 'USPS', 'usps first class mail': 'USPS',
    'u s mail': 'USPS', 'u s post office': 'USPS',
    'ups': 'UPS', 'united parcel service': 'UPS', 'ups ground': 'UPS', 'ups mail innovations': 'UPS', 'ups surepost': 'UPS', 'ups sure post': 'UPS',
    'fedex': 'FedEx', 'fed ex': 'FedEx', 'federal express': 'FedEx', 'fedex ground': 'FedEx', 'fedex home delivery': 'FedEx',
    'fedex smartpost': 'FedEx', 'fedex ground economy': 'FedEx',
    'dhl': 'DHL', 'dhl express': 'DHL', 'dhl ecommerce': 'DHL eCommerce', 'dhl e commerce': 'DHL eCommerce', 'dhl ecommerce us': 'DHL eCommerce',
    'dhl ecommerce solutions': 'DHL eCommerce', 'dhl ecommerce asia': 'DHL eCommerce', 'dhl global mail': 'DHL eCommerce',
    'ontrac': 'OnTrac', 'on trac': 'OnTrac', 'lasership': 'LaserShip', 'laser ship': 'LaserShip',
    'amazon shipping': 'Amazon Shipping', 'amazon logistics': 'Amazon Shipping', 'amazon logistics us': 'Amazon Shipping', 'amazon logistics uk': 'Amazon Shipping',
    'amzl': 'Amazon Shipping',
    'canada post': 'Canada Post', 'postes canada': 'Canada Post', 'royal mail': 'Royal Mail', 'parcelforce': 'Parcelforce', 'parcel force': 'Parcelforce',
    'purolator': 'Purolator', 'canpar': 'Canpar', 'australia post': 'Australia Post', 'auspost': 'Australia Post',
    'new zealand post': 'NZ Post', 'nz post': 'NZ Post', 'gls': 'GLS', 'gls us': 'GLS', 'dpd': 'DPD', 'evri': 'Evri',
    'veho': 'Veho', 'uniuni': 'UniUni', 'uni uni': 'UniUni', 'better trucks': 'Better Trucks', 'cdl last mile': 'CDL Last Mile',
    'speedee delivery': 'Spee-Dee Delivery', 'spee dee delivery': 'Spee-Dee Delivery', 'spee dee': 'Spee-Dee Delivery', 'speedee': 'Spee-Dee Delivery',
    'lso': 'LSO', 'lone star overnight': 'LSO',
    'gofo': 'GOFO', 'intelcom': 'Intelcom', 'axlehire': 'AxleHire', 'axle hire': 'AxleHire', 'cirro': 'CIRRO', 'jitsu': 'Jitsu', 'speedx': 'SpeedX'
  };
  var IDS = { timeline: 'mk-order-timeline', banner: 'mk-delivery-banner', slot: 'mk-delivery-banner-slot', schedule: 'mk-tl-schedule', title: 'mk-tl-title', orderLine: 'mk-oc-',
    pop: 'mk-track' };
  var EVENTS_IN_ORDERS = (M.track && M.track.EVENT_CAP) || 10;   /* /v1/orders gives at most this many scans per package (Appendix A) */
  var EVENTS_MAX = 100;            /* /v1/tracking: at most this many per package */
  var POP_PACKAGES_MAX = 5;        /* tabs in the pop-up (the service lists at most 5 shipments) */
  /* Shopify's scan status (FulfillmentEventStatus) in plain words; anything else: 'Tracking update' */
  var EVENT_WORDS = {
    LABEL_PRINTED: 'Label created', LABEL_PURCHASED: 'Label created', CONFIRMED: 'Label created', SUBMITTED: 'Label created',
    CARRIER_PICKED_UP: 'Picked up by the carrier', IN_TRANSIT: 'In transit', DELAYED: 'Delayed',
    OUT_FOR_DELIVERY: 'Out for delivery', ATTEMPTED_DELIVERY: 'Delivery attempted', READY_FOR_PICKUP: 'Ready for pickup',
    PICKED_UP: 'Picked up', DELIVERED: 'Delivered', FAILURE: 'Delivery problem', NOT_DELIVERED: 'Not delivered',
    FULFILLED: 'Shipped', MARKED_AS_FULFILLED: 'Shipped'
  };
  /* approved wording (TRACKING-NOTES.md, Jim's decisions of 2026-10-12): one verb "Track package" everywhere */
  var COPY_TRACK = {
    open: 'Track package', openMany: 'Track packages',
    title: 'Scan history', newest: 'Newest first', region: 'Tracking details', packages: 'Packages',
    all: 'Show all', retry: 'Try again',
    loadingStatus: 'Loading earlier updates\u2026',           /* spinner line / live region while loading */
    error: 'Earlier updates could not be loaded.',
    notFound: 'Earlier updates are not available here.',
    notFoundLink: ' The tracking site may have them.',          /* r6 (C7): a ShipBob number opens ShipBob's page, not the carrier's */
    loaded: 'Earlier updates loaded.',                         /* screen readers only */
    moreScans: 'Only the newest 100 updates of this package are shown.',
    moreShipmentsPop: 'This order has more packages than shown here.',
    none: 'The carrier has not scanned this package yet. Updates appear here after the first scan.',
    carrierLink: 'View on the tracking site', newTab: ' (opens in a new tab)'
  };
  /* r6 scan notes (C1): the status word is shown as a tag beside the carrier's note only when the note does not
     already say it; any status not listed here (in transit, picked up by the carrier, label, unknown) never gets one */
  var TAG_RULES = {
    OUT_FOR_DELIVERY: /\bout\s+for\s+delivery\b/i, DELIVERED: /\bdelivered\b/i, ATTEMPTED_DELIVERY: /\battempt/i, DELAYED: /\bdelay/i,
    READY_FOR_PICKUP: /\bpick\s?-?up\b|\bavailable\s+for\b/i, PICKED_UP: /\bpicked\s+up\b/i,
    FAILURE: /\bexception\b|\bfail|\bproblem\b|\bunable\b/i, NOT_DELIVERED: /\bnot\s+delivered\b|\bundeliverable\b|\breturn/i
  };
  var ALERT_EVENTS = /^(ATTEMPTED_DELIVERY|DELAYED|FAILURE|NOT_DELIVERED)$/;   /* their tag in the alert colour */
  var NOTE_MAX_CLIENT = 140;       /* code points; the service sends at most 120 (with its "\u2026" when cut) */
  /* all-capitals notes are shown in sentence case. TWO switches, both NOTE_SENTENCE_CASE: the service's (worker.js)
     does the casing; this one only re-cases all-capitals text the service passed through. Set both to false to show
     the carrier's text as written. These words keep the carrier's own spelling */
  var NOTE_SENTENCE_CASE = true;
  var NOTE_KEEP = { usps: 'USPS', ups: 'UPS', dhl: 'DHL', fedex: 'FedEx', ontrac: 'OnTrac', lasership: 'LaserShip', gofo: 'GOFO',
    uniuni: 'UniUni', veho: 'Veho', amazon: 'Amazon', smartpost: 'SmartPost', surepost: 'SurePost', ecommerce: 'eCommerce', po: 'PO', id: 'ID' };
  var STATE_CODES = ' al ak az ar ca co ct de dc fl ga hi id il in ia ks ky la me md ma mi mn ms mo mt ne nv nh nj nm ny nc nd oh ok or pa pr ri sc sd tn tx ut vt va wa wv wi wy ';
  /* state codes that are also ordinary scan words (the service's NOTE_WORDS and STATE_CODES in common): upper-cased
     only when they end the note ("Arrived in Indianapolis, IN", never "Delivered, IN mailbox") */
  var NOTE_CODE_WORDS = ' id in or ';

  /* ------------------------------------------------------------------ */
  /* GraphQL (customer role, read-only)                                   */
  /* ------------------------------------------------------------------ */
  /* this subscription: fields from Skio's own getSubscriptionById / line fragment */
  var Q_SUB = [
    'query mkDeliverySub($subscriptionId: uuid!) {',
    '  SubscriptionByPk(id: $subscriptionId) {',
    '    id siteId status statusContext nextBillingDate cyclesCompleted streakCount streakExpiresAt',
    '    skipCycleBillingDate isDigital prepaidGiftRecipientId prepaidDeliveryPolicyId',
    '    BillingPolicy { interval intervalCount maxCycles isMaxCycleV2 Anchors { type day } }',
    '    SubscriptionLines(where: {removedAt: {_is_null: true}}) {',
    '      id quantity customAttributes isPrepaid ordersRemaining titleOverride',
    '      ProductVariant { title Product { title } }',
    '    }',
    '  }',
    '}'
  ].join('\n');
  /* every subscription: Skio's own list query, word for word (spec 3.1) */
  var Q_LIST = [
    'query getSubscriptionsList($storefrontUserId: uuid!) {',
    '  Subscriptions(',
    '    where: {_or: [{storefrontUserId: {_eq: $storefrontUserId}}, {prepaidGiftRecipientId: {_eq: $storefrontUserId}}], _and: {_or: [{statusContext: {_is_null: true}}, {statusContext: {_neq: "PERMANENTLY_CANCELLED"}}]}}',
    '    order_by: [{status: asc}, {nextBillingDate: asc}]',
    '  ) {',
    '    id', '    status', '    statusContext', '    nextBillingDate', '    currencyCode', '    deliveryPrice',
    '    deliveryPriceOverride', '    isDigital', '    cyclesCompleted', '    prepaidGiftRecipientId', '    customAttributes',
    '    BillingPolicy {', '      interval', '      intervalCount', '    }',
    '    SubscriptionLines(where: {removedAt: {_is_null: true}}) {',
    '      id', '      quantity', '      platformId', '      priceWithoutDiscount', '      customAttributes', '      updatedAt',
    '      sellingPlanId', '      ProductVariant {', '        platformId', '        title', '        image',
    '        Product {', '          title', '          imageSrc', '        }', '      }',
    '      Discounts {', '        id', '        fixedValue', '        percentage', '        appliesOnEachItem', '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');
  /* optional: current tier, tier ladder and the store's time zone (spec 3.5 / 3.8 fields) */
  var Q_LOYALTY = [
    'query mkDeliveryLoyalty($storefrontUserId: uuid!, $siteId: uuid!) {',
    '  StorefrontUserByPk(id: $storefrontUserId) {',
    '    StorefrontUserTiers(where: {removedAt: {_is_null: true}}, order_by: {createdAt: desc_nulls_last}) { TierLevel { id name } }',
    '  }',
    '  SiteByPk(id: $siteId) {',
    '    id ianaTimezone',
    '    TierGroups(where: {removedAt: {_is_null: true}}) {',
    '      TierLevels(where: {removedAt: {_is_null: true}, enabled: {_eq: true}}, order_by: {orderIndex: desc}) { name eligibility }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  /* ------------------------------------------------------------------ */
  /* small helpers                                                        */
  /* ------------------------------------------------------------------ */
  var DAY_MS = 86400000;
  var STATUSES = /^(processing|label_created|shipped|in_transit|out_for_delivery|delayed|attempted_delivery|problem|ready_for_pickup|picked_up|delivered|canceled)$/;
  var UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  function str(v, max) { return typeof v === 'string' && v.trim() ? v.trim().slice(0, max || 200) : null; }
  function ms(v) { if (typeof v !== 'string') return null; var t = Date.parse(v); return isFinite(t) ? t : null; }
  /* a full timestamp from the service (shippedAt, deliveredAt, outForDeliveryAt, a scan's happenedAt) as a real
     instant: the core reads a string ending in T00:00:00Z as a date-only value (Skio's dates), which would put a
     scan at exactly midnight UTC on the wrong day in the Americas. The order's processedAt too. estimatedDeliveryAt
     stays a string (Shopify gives a carrier's date-only estimate as UTC midnight: a calendar day). */
  function inst(v) { var t = ms(v); return t === null ? null : new Date(t); }
  function int(v) { var n = typeof v === 'number' ? v : (typeof v === 'string' && /^-?\d+$/.test(v.trim()) ? parseInt(v, 10) : NaN); return isFinite(n) ? Math.round(n) : null; }
  function norm(t) { return String(t || '').toLowerCase().replace(/\s+/g, ' ').trim(); }
  function plural(n, one, many) { return n === 1 ? one : many; }
  /* tracking company -> the carrier's display name, or null (not a known parcel carrier: not shown) */
  function carrierName(company) {
    if (typeof company !== 'string') return null;
    var k = company.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    return k && Object.prototype.hasOwnProperty.call(CARRIERS, k) ? CARRIERS[k] : null;
  }
  /* the service's carrier field (2.2): a short plain name, never the warehouse */
  function validCarrier(v) {
    var t = typeof v === 'string' ? v.trim() : '';
    return /^[A-Za-z][A-Za-z0-9 .&'-]{0,29}$/.test(t) && !/ship\s*bob/i.test(t) ? t : null;
  }
  function andList(a) { return a.length < 2 ? (a[0] || '') : a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1]; }
  function shippedWith(names) { return names.length ? 'Shipped with ' + andList(names) : ''; }
  function uniq(a) { var seen = {}, out = []; a.forEach(function (x) { if (x && !seen[x]) { seen[x] = 1; out.push(x); } }); return out; }
  function hash(s) { var h = 5381; for (var i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0; return (h >>> 0).toString(36); }
  function validTz(tz) {
    if (typeof tz !== 'string' || !/^[A-Za-z_]+(\/[A-Za-z0-9_+-]+){0,2}$/.test(tz)) return null;
    try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); return tz; } catch (e) { return null; }
  }
  /* dates and schedule rules live in the core (MKPortal.schedule, round 2 spec BC 4.2), shared with
     the home module's Upcoming orders screen; these names keep this file's code unchanged */
  var SCHED = M.schedule;
  function ymdOf(d) { return SCHED.ymd(d); }
  function siteYmd(v, tz) { return SCHED.siteYmd(v, tz); }
  var WEEKDAY_LONG = new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: 'UTC' });
  /* "Friday, Oct 16" (headline style) */
  function longDay(v, opts) {
    var cd = M.calendarDay(v, opts);
    if (!cd) return '';
    var s = M.formatDate(v, 'short', opts);
    return s ? WEEKDAY_LONG.format(new Date(Date.UTC(cd.y, cd.m - 1, cd.d, 12))) + ', ' + s : '';
  }
  /* "today" / "tomorrow" / "yesterday" or the given style */
  function relDay(v, style, opts) {
    var n = M.daysFromToday(v, opts);
    if (n === 0) return 'today';
    if (n === 1) return 'tomorrow';
    if (n === -1) return 'yesterday';
    return style === 'long' ? longDay(v, opts) : M.formatDate(v, style || 'weekday', opts);
  }
  function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
  function itemsText(titles, max) {
    var t = uniq(titles);
    if (!t.length) return '';
    if (t.length <= max) return t.join(', ');
    return t.slice(0, max).join(', ') + ' and ' + (t.length - max) + ' more';
  }
  function median(a) {
    if (!a.length) return null;
    var s = a.slice().sort(function (x, y) { return x - y; }), h = Math.floor(s.length / 2);
    return s.length % 2 ? s[h] : Math.round((s[h - 1] + s[h]) / 2);
  }
  /* 'every month' (lower case, inside a sentence) */
  function scheduleLabel(interval, count) {
    var l = SCHED.label(interval, count);
    return l ? l.charAt(0).toLowerCase() + l.slice(1) : '';
  }


  /* ------------------------------------------------------------------ */
  /* delivery-service data                                                */
  /* ------------------------------------------------------------------ */
  function normFulfillment(f) {
    if (!f || typeof f !== 'object') return null;
    var state = STATUSES.test(f.state) ? f.state : null;
    var tracking = (Array.isArray(f.tracking) ? f.tracking : []).slice(0, 2).map(function (t) {
      if (!t || typeof t !== 'object') return null;
      return { company: str(t.company, 60), number: str(t.number, 60), url: M.safeUrl(t.url, { absolute: true }) };
    }).filter(Boolean);
    var ev = f.lastEvent && typeof f.lastEvent === 'object' ? f.lastEvent : null;
    /* service 2.2: "carrier" (null included) and "events"; 2.1 answers have neither */
    var v22 = Object.prototype.hasOwnProperty.call(f, 'carrier') || Array.isArray(f.events);
    return {
      v22: v22,
      carrier: v22 ? validCarrier(f.carrier) : null,
      events: Array.isArray(f.events) ? normEvents(f.events, EVENTS_IN_ORDERS) : null,
      state: state,
      shippedAt: ms(f.shippedAt) ? f.shippedAt : null,
      outForDeliveryAt: ms(f.outForDeliveryAt) ? f.outForDeliveryAt : null,
      deliveredAt: ms(f.deliveredAt) ? f.deliveredAt : null,
      estimatedDeliveryAt: ms(f.estimatedDeliveryAt) ? f.estimatedDeliveryAt : null,
      tracking: tracking,
      lastEvent: ev ? { at: ms(ev.happenedAt) ? ev.happenedAt : null, city: str(ev.city, 60), province: str(ev.province, 40) } : null
    };
  }
  /* one scan: the four fields of the service 2.2 plus the 2.3 "note" (null when absent); anything malformed is dropped */
  function normEvent(e) {
    if (!e || typeof e !== 'object' || !ms(e.happenedAt)) return null;
    var city = str(e.city, 80), province = str(e.province, 80);
    return { status: typeof e.status === 'string' && /^[A-Z_]{2,40}$/.test(e.status) ? e.status : null, at: e.happenedAt,
      city: city, province: province, note: noteCase(cleanNote(e.note), city, province) };
  }
  /* the service's note, checked again (defence in depth; it already cleaned it): printable ASCII and its "\u2026"
     only, letters, at most NOTE_MAX_CLIENT code points; anything that looks like an e-mail, a link, markup, an entity,
     a phone or a long number, the warehouse or the store is not shown (null: the row keeps the status words) */
  function cleanNote(v) {
    if (typeof v !== 'string') return null;
    var s = v.replace(/[\u0000-\u001f\u007f-\u009f\u00ad\u200b-\u200f\u2028-\u202e\u2060-\u206f\ufeff]/g, ' ').replace(/\s+/g, ' ').trim();
    if (/[^\x20-\x7e\u2026]/.test(s) || s.length < 3 || s.length > NOTE_MAX_CLIENT || !/[A-Za-z]{2}/.test(s)) return null;
    if (/[@<>]|&#?\w+;|https?:|www\.|\d{5,}|\d{3}\D{0,2}\d{3}\D?\d{4}|ship\s*bob|trackbob|\bmk\b/i.test(s)) return null;
    /* a phone or tracking number split into short groups ("512 / 555 / 0142", "9400 1111 2222 ..."), as the service */
    if (/\d{7,}/.test(s.replace(/[^A-Za-z\d]+/g, ''))) return null;
    return s;
  }
  /* an all-capitals note ("DELIVERED, FRONT DOOR/PORCH") in sentence case ("Delivered, front door/porch"): carrier
     names keep their spelling (NOTE_KEEP), the scan's own city and state the service's spelling, a state code after a
     comma and the country ("US Mail", "in the US") stay capitals; a note with any lower-case letter stays as written */
  function noteCase(s, city, province) {
    if (!s || !NOTE_SENTENCE_CASE || /[a-z]/.test(s) || (s.match(/[A-Z]/g) || []).length < 4) return s;
    var out = s.toLowerCase().replace(/[a-z]+/g, function (w) { return Object.prototype.hasOwnProperty.call(NOTE_KEEP, w) ? NOTE_KEEP[w] : w; });
    out = out.replace(/\bus\b(?=\s+(?:mail|postal|post|customs|border)\b)/g, 'US').replace(/\b(the|in|into|entered)\s+us\b(?=[\s.,;:]|$)/g, '$1 US');
    [city, province].forEach(function (p) {
      if (!p || !/[a-z]/.test(p)) return;
      out = out.replace(new RegExp('\\b' + p.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'g'), function () { return p; });
    });
    out = out.replace(/(,\s*)([a-z]{2})\b/g, function (m, a, c, i) {
      var end = /^[\s.]*$/.test(out.slice(i + m.length));
      return STATE_CODES.indexOf(' ' + c + ' ') >= 0 && (NOTE_CODE_WORDS.indexOf(' ' + c + ' ') < 0 || end) ? a + c.toUpperCase() : m;
    });
    if (province && /^[A-Z]{2}$/.test(province)) {
      var pc = province.toLowerCase();
      out = out.replace(new RegExp('\\b' + pc + (NOTE_CODE_WORDS.indexOf(' ' + pc + ' ') >= 0 ? '(?=[\\s.]*$)' : '\\b'), 'g'), province);
    }
    return out.replace(/(^|[.!?]\s+)([a-z])/g, function (m, a, b) { return a + b.toUpperCase(); });
  }
  function normEvents(list, max) {
    return list.map(normEvent).filter(Boolean).sort(function (a, b) { return ms(b.at) - ms(a.at); }).slice(0, max);
  }
  /* -> valid orders, newest first; anything malformed is dropped */
  function normalizeOrders(body) {
    if (!body || !Array.isArray(body.orders)) return [];
    return body.orders.map(function (o) {
      if (!o || typeof o !== 'object' || !STATUSES.test(o.status) || !ms(o.processedAt)) return null;
      var name = str(o.name, 40);
      if (!name) return null;
      var items = (Array.isArray(o.items) ? o.items : []).map(function (i) {
        if (!i || typeof i !== 'object') return null;
        var title = str(i.title, 160);
        var q = int(i.quantity);
        return title ? { title: title, quantity: q && q > 0 ? q : 1 } : null;
      }).filter(Boolean);
      return {
        name: name, processedAt: o.processedAt, status: o.status, partiallyShipped: o.partiallyShipped === true,
        items: items, fulfillments: (Array.isArray(o.fulfillments) ? o.fulfillments : []).map(normFulfillment).filter(Boolean)
      };
    }).filter(Boolean).sort(function (a, b) { return ms(b.processedAt) - ms(a.processedAt); });
  }
  /* the shipment that explains the order's status */
  function primary(o) {
    var fs = o.fulfillments;
    if (!fs.length) return null;
    if (o.status === 'delivered' || o.status === 'picked_up') {
      return fs.slice().sort(function (a, b) { return (ms(b.deliveredAt) || 0) - (ms(a.deliveredAt) || 0); })[0];
    }
    for (var i = 0; i < fs.length; i++) if (fs[i].state === o.status) return fs[i];
    return fs[0];
  }
  function allTracking(o) {
    var out = [], seen = {};
    o.fulfillments.forEach(function (f) {
      f.tracking.forEach(function (t) {
        var k = t.url || t.number || t.company;
        if (!k || seen[k]) return;
        seen[k] = 1;
        out.push(t);
      });
    });
    return out.slice(0, 2);
  }
  function itemCount(o) { return o.items.reduce(function (s, i) { return s + i.quantity; }, 0); }
  /* a future estimate only: a date that already passed is not an estimate any more */
  function futureEta(f, opts) {
    var e = f && f.estimatedDeliveryAt;
    if (!e) return null;
    var n = M.daysFromToday(e, opts);
    return n !== null && n >= 0 ? e : null;
  }
  /* customer's own history: order date -> delivered (days), shipped -> delivered (days) */
  function history(orders, opts) {
    var orderToDoor = [], shipToDoor = [];
    orders.forEach(function (o) {
      if (o.status !== 'delivered') return;
      var f = primary(o);
      if (!f || !f.deliveredAt) return;
      var a = M.daysBetween(inst(o.processedAt), inst(f.deliveredAt), opts);
      if (a !== null && a >= 0 && a <= ARRIVAL_MAX_DAYS) orderToDoor.push(a);
      o.fulfillments.forEach(function (g) {
        if (!g.shippedAt || !g.deliveredAt) return;
        var b = M.daysBetween(inst(g.shippedAt), inst(g.deliveredAt), opts);
        if (b !== null && b >= 0 && b <= ARRIVAL_MAX_DAYS) shipToDoor.push(b);
      });
    });
    return {
      orderToDoor: orderToDoor.length >= ARRIVAL_MIN_SAMPLES ? median(orderToDoor) : null,
      orderSamples: orderToDoor.length,
      shipMin: shipToDoor.length >= USUAL_MIN_SAMPLES ? Math.min.apply(null, shipToDoor) : null,
      shipMax: shipToDoor.length >= USUAL_MIN_SAMPLES ? Math.max.apply(null, shipToDoor) : null
    };
  }

  /* ------------------------------------------------------------------ */
  /* Skio data                                                            */
  /* ------------------------------------------------------------------ */
  /* full = from mkDeliverySub (policy, streak, prepaid known); otherwise from the list query */
  function normSub(s, full) { return SCHED.normalize(s, full); }
  function titlesOf(sub) {
    var t = {};
    sub.lines.forEach(function (l) { t[norm(l.title)] = 1; if (l.productTitle) t[norm(l.productTitle)] = 1; });
    return t;
  }
  /* tier ladder from GraphQL, accepted only when it is MK's known ladder (core TIERS) */
  function parseLoyalty(res) {
    var out = { tz: null, ladder: null, current: null };
    try {
      var d = res && !res.errors && res.data;
      if (!d) return out;
      var site = d.SiteByPk || {};
      out.tz = validTz(site.ianaTimezone);
      var levels = [];
      (Array.isArray(site.TierGroups) ? site.TierGroups : []).forEach(function (g) {
        (g && Array.isArray(g.TierLevels) ? g.TierLevels : []).forEach(function (l) { levels.push(l); });
      });
      var ladder = levels.map(function (l) {
        var t = M.tierFor(l && l.name);
        var c = l && l.eligibility && Array.isArray(l.eligibility.conditions) ? l.eligibility.conditions : null;
        if (!t || !c) return null;
        if (c.length === 0 && t.minStreak === 0) return { key: t.key, name: t.name, min: 0 };
        if (c.length !== 1 || !c[0] || c[0].name !== 'STREAK' || !c[0].value) return null;
        var n = int(c[0].value.streakCount);
        return n === null ? null : { key: t.key, name: t.name, min: n };
      });
      var ok = ladder.length === M.TIERS.length && ladder.every(function (x) { return x; }) &&
        M.TIERS.every(function (t) { return ladder.some(function (x) { return x.key === t.key && x.min === t.minStreak; }); });
      if (ok) out.ladder = ladder.sort(function (a, b) { return a.min - b.min; });
      var user = d.StorefrontUserByPk;
      var tiers = user && Array.isArray(user.StorefrontUserTiers) ? user.StorefrontUserTiers : [];
      var cur = tiers[0] && tiers[0].TierLevel ? M.tierFor(tiers[0].TierLevel.name) : null;
      out.current = cur ? cur.key : null;
    } catch (e) { /* fail closed: no ladder */ }
    return out;
  }
  /* Skio's own streak badge (both copies, desktop and phone), or null */
  function streakBadge() {
    var vals = M.qa('#skio-root [data-skio-name="streak-badge-count"]').map(function (n) { return int((n.textContent || '').trim()); });
    if (!vals.length || vals.some(function (v) { return v === null; })) return null;
    return vals.every(function (v) { return v === vals[0]; }) ? vals[0] : null;
  }
  function contactUrl() {
    var links = M.qa('a[href]').filter(function (a) { return !a.closest('#skio-root, #skio-portal, [data-mk-module]'); });
    for (var i = 0; i < links.length; i++) {
      var u = M.safeUrl(links[i].getAttribute('href'), { sameOrigin: true });
      if (u && /^\/pages\/contact/i.test(new URL(u).pathname)) return u;
    }
    return M.safeUrl(CONTACT_FALLBACK, { sameOrigin: true });
  }

  /* ------------------------------------------------------------------ */
  /* models (pure: data in, plain objects out)                            */
  /* ------------------------------------------------------------------ */
  /* which orders belong on this subscription's page */
  function ordersForSub(orders, sub, subsCount) {
    var t = titlesOf(sub);
    var matched = orders.filter(function (o) { return o.items.some(function (i) { return t[norm(i.title)]; }); });
    if (matched.length) return { orders: matched, how: 'matched' };
    if (subsCount === 1 && orders.length) return { orders: orders, how: 'single-subscription' };
    return null;
  }
  function openSub(s) { return SCHED.open(s); }
  /* "Coming up": at most UPCOMING_COUNT dates, the next order included (spec 11.6; the rules and the
     month-end check are in MKPortal.schedule.upcoming) */
  function upcoming(sub, tz) { return SCHED.upcoming(sub, tz, { max: UPCOMING_COUNT }); }
  function stepNote(parts) { return parts.filter(function (p) { return p; }); }

  function headline(o, f, opts) {
    var eta = futureEta(f, opts);
    switch (o.status) {
      case 'delivered': return { text: 'Delivered ' + longDay(inst(f && f.deliveredAt || o.processedAt), opts), tone: 'done' };
      case 'picked_up': return { text: 'Picked up ' + longDay(inst(f && f.deliveredAt || o.processedAt), opts), tone: 'done' };
      case 'out_for_delivery':
        return { text: f && f.outForDeliveryAt && M.daysFromToday(inst(f.outForDeliveryAt), opts) === 0 ? 'Out for delivery today' : 'Out for delivery', tone: 'live' };
      case 'shipped': case 'in_transit':
        return { text: eta ? 'On its way: arriving ' + relDay(eta, 'long', opts) : 'On its way', tone: 'live' };
      /* r6 (C7): one tracking verb, "Track package"; no second tracking hint (it showed even without a Track package button) */
      case 'delayed': return { text: 'Delayed', tone: 'alert' };
      case 'attempted_delivery': return { text: 'Delivery attempted', tone: 'alert' };
      case 'ready_for_pickup': return { text: 'Ready for pickup', tone: 'live' };
      case 'problem': return { text: 'Delivery problem: ', contact: 'contact us', tone: 'alert' };
      case 'label_created': return { text: 'Getting ready to ship', tone: 'prep' };
      case 'processing': return { text: 'Preparing your order', tone: 'prep' };
    }
    return null;
  }
  /* the carriers to name: the service's field (2.2), else real carriers among the tracking companies (2.1, CR-2) */
  function carriersOf(o) {
    if (o.fulfillments.some(function (f) { return f.v22; })) return uniq(o.fulfillments.map(function (f) { return f.carrier; }));
    return uniq(allTracking(o).map(function (x) { return carrierName(x.company); }));
  }
  /* the carrier's tracking page of a shipment: its first https tracking URL (MKPortal.track.url), or null */
  function shipUrl(f) {
    if (!f) return null;
    if (M.track && typeof M.track.url === 'function') return M.track.url(f);
    var t = (f.tracking || []).filter(function (x) { return x && x.url && /^https:\/\//i.test(x.url); })[0];
    return t ? t.url : null;
  }
  function trackingNote(o) {
    var t = allTracking(o);
    var companies = carriersOf(o);
    var links = t.filter(function (x) { return x.url && /^https:\/\//i.test(x.url); }).map(function (x) { return { href: x.url }; });
    var numbers = links.length ? [] : t.filter(function (x) { return x.number; }).map(function (x) { return 'Tracking ' + x.number; });
    return { companies: companies, carrierText: shippedWith(companies), links: links, numbers: numbers };
  }
  /* "Track package" is offered for an order with a shipment that has scans (2.2) or a carrier tracking page */
  function canTrack(o) {
    return !!o && o.fulfillments.length > 0 && (hasEvents(o) || o.fulfillments.some(function (f) { return !!shipUrl(f); }));
  }
  /* has the order (or a package) left the warehouse? processing / label created: not yet */
  function shipped(state) { return typeof state === 'string' && state !== 'processing' && state !== 'label_created'; }
  var LABEL_EVENTS = /^(LABEL_PRINTED|LABEL_PURCHASED|CONFIRMED|SUBMITTED)$/;
  function lastScan(f) {
    var e = f && f.lastEvent;
    return e && e.city && e.province ? 'Last scan: ' + e.city + ', ' + e.province : null;
  }
  function steps(o, f, sub, tz, opts) {
    var out = [];
    var st = o.status, tr = trackingNote(o);
    out.push({ key: 'ordered', state: 'done', label: 'Ordered', date: M.formatDate(inst(o.processedAt), 'weekday', opts), note: [] });
    /* shipped */
    if (st === 'processing' || !f) {
      out.push({ key: 'shipped', state: 'upcoming', label: 'Shipped', date: '', note: ['Not shipped yet'] });
    } else if (st === 'label_created') {
      out.push({ key: 'shipped', state: 'current', label: 'Label created', date: f.shippedAt ? M.formatDate(inst(f.shippedAt), 'weekday', opts) : '', note: ['Not scanned by the carrier yet'] });
    } else {
      /* the carrier only: tracking is the one "Track package" action under the steps (batch 3) */
      out.push({ key: 'shipped', state: 'done', label: 'Shipped', date: f.shippedAt ? M.formatDate(inst(f.shippedAt), 'weekday', opts) : '',
        note: stepNote([tr.carrierText].concat(tr.numbers)) });
    }
    /* delivery */
    var eta = futureEta(f, opts);
    var lastAt = inst(f && f.lastEvent && f.lastEvent.at);
    if (st === 'delivered' || st === 'picked_up') {
      out.push({ key: 'delivery', state: 'done', label: st === 'picked_up' ? 'Picked up' : 'Delivered', date: f && f.deliveredAt ? M.formatDate(inst(f.deliveredAt), 'weekday', opts) : '', note: [] });
    } else if (st === 'out_for_delivery') {
      var ofd = inst(f && f.outForDeliveryAt);
      out.push({ key: 'delivery', state: 'current', label: 'Out for delivery', date: ofd ? cap(relDay(ofd, 'weekday', opts)) : '', note: stepNote([lastScan(f)]) });
    } else if (st === 'shipped' || st === 'in_transit') {
      out.push(eta ? { key: 'delivery', state: 'current', label: 'Estimated arrival', date: cap(relDay(eta, 'weekday', opts)), note: stepNote([lastScan(f)]) }
        : { key: 'delivery', state: 'current', label: 'In transit', date: '', note: stepNote([lastScan(f) || 'No arrival estimate yet']) });
    } else if (st === 'delayed') {
      /* r6 (C7): the last scan's place when known (as in transit), never a second tracking verb */
      out.push({ key: 'delivery', state: 'alert', label: 'Delayed', date: lastAt ? M.formatDate(lastAt, 'weekday', opts) : '', note: stepNote([lastScan(f)]) });
    } else if (st === 'attempted_delivery') {
      out.push({ key: 'delivery', state: 'alert', label: 'Delivery attempted', date: lastAt ? M.formatDate(lastAt, 'weekday', opts) : '', note: stepNote([lastScan(f)]) });
    } else if (st === 'ready_for_pickup') {
      out.push({ key: 'delivery', state: 'current', label: 'Ready for pickup', date: lastAt ? M.formatDate(lastAt, 'weekday', opts) : '', note: [] });
    } else if (st === 'problem') {
      out.push({ key: 'delivery', state: 'alert', label: 'Delivery problem', date: lastAt ? M.formatDate(lastAt, 'weekday', opts) : '', note: [], contact: true });
    } else {
      out.push({ key: 'delivery', state: 'upcoming', label: 'Delivery', date: eta ? 'Estimated ' + M.formatDate(eta, 'weekday', opts) : '', note: eta ? [] : ['After it ships'] });
    }
    /* next order (from Skio) */
    if (sub.status === 'ACTIVE' && sub.next && sub.statusContext === 'TEMPORARILY_PAUSED') {
      out.push({ key: 'next', state: 'next', label: 'Resumes', date: M.formatDate(siteYmd(sub.next, tz), 'weekday', opts), note: [] });
    } else if (sub.status === 'ACTIVE' && sub.next) {
      out.push({ key: 'next', state: 'next', label: 'Next order', date: M.formatDate(siteYmd(sub.next, tz), 'weekday', opts), note: ['Change anything before then'] });
    } else if (sub.status === 'PAUSED') {
      out.push({ key: 'next', state: 'next', label: 'Next order', date: '', note: ['Paused'] });
    }
    return out;
  }

  /* ---- scans (service 2.2): the Track package pop-up ---- */
  function hasEvents(o) { return !!o && o.fulfillments.some(function (f) { return f.events && f.events.length > 0; }); }
  var timeFmt = {};
  function clock(v, opts) {
    var tz = opts && opts.timeZone, k = tz || '';
    try {
      if (!timeFmt[k]) timeFmt[k] = new Intl.DateTimeFormat('en-US', tz ? { hour: 'numeric', minute: '2-digit', timeZone: tz } : { hour: 'numeric', minute: '2-digit' });
      return timeFmt[k].format(new Date(ms(v)));
    } catch (e) { return ''; }
  }
  /* the pop-up's day heading: "Today, Oct 12" / "Yesterday, Oct 11" / "Saturday, Oct 10" */
  function dayHead(v, opts) {
    var d = inst(v), n = M.daysFromToday(d, opts);
    if (n === 0) return 'Today, ' + M.formatDate(d, 'short', opts);
    if (n === -1) return 'Yesterday, ' + M.formatDate(d, 'short', opts);
    return longDay(d, opts);
  }
  /* words: the status in plain words (the label-only note and "Show all" logic keep using them); text: the row's main
     line, the carrier's note when there is one (r6), else the words; tag: the words beside a note that does not say them */
  function eventRow(e, opts) {
    var words = e.status && Object.prototype.hasOwnProperty.call(EVENT_WORDS, e.status) ? EVENT_WORDS[e.status] : 'Tracking update';
    return { words: words, text: e.note || words, tag: e.note ? eventTag(e.status, e.note) : null, alert: ALERT_EVENTS.test(e.status || ''),
      at: e.at, where: [e.city, e.province].filter(function (x) { return x; }).join(', '),
      time: clock(e.at, opts), day: M.formatDate(inst(e.at), 'iso', opts), dayHead: dayHead(e.at, opts) };
  }
  function eventTag(status, note) {
    var rule = status && Object.prototype.hasOwnProperty.call(TAG_RULES, status) ? TAG_RULES[status] : null;
    return rule && !rule.test(note) ? EVENT_WORDS[status] : null;
  }
  /* has this package left the warehouse? its state (from /v1/orders) or, without one, a scan past the label stage */
  function packageGone(f) {
    return f.state ? shipped(f.state) : (f.events || []).some(function (e) { return !LABEL_EVENTS.test(e.status || ''); });
  }
  /* a /v1/tracking answer for this order: the order's shipments with all their scans. Shipments are matched by
     tracking number (by position only when neither side has numbers; MKPortal.track.merge): each endpoint caches
     for minutes, so a shipment added between the two answers would otherwise attach one package's scans to
     another. -> [{carrier, moreScans, full, events (normalized), state}] | null (malformed / another order) */
  function mergeTracking(o, body) {
    if (!o || !body || typeof body !== 'object' || !Array.isArray(body.fulfillments) || str(body.order, 40) !== o.name) return null;
    if (body.fulfillments.some(function (f) { return !f || typeof f !== 'object' || !Array.isArray(f.events); })) return null;
    var data = { order: body.order, moreShipments: body.moreShipments === true, fulfillments: body.fulfillments.slice(0, POP_PACKAGES_MAX) };
    var merged = M.track && typeof M.track.merge === 'function' ? M.track.merge(o, data) : null;
    if (!Array.isArray(merged) || merged.length !== o.fulfillments.length) return null;
    return merged.map(function (m, i) {
      var f = o.fulfillments[i];
      var full = !!(m && m.full);
      return { state: f.state,
        carrier: full && validCarrier(m.carrier) ? validCarrier(m.carrier) : f.carrier,
        moreScans: full && m.moreScans === true, full: full,
        events: full ? normEvents(Array.isArray(m.events) ? m.events : [], EVENTS_MAX) : (f.events || []) };
    });
  }

  /* ---- the Track package pop-up's model (pure) ---- */
  /* 0 ordered, 1 shipped, 2 out for delivery, 3 delivered; a half = between two steps */
  function stage(state) {
    switch (state) {
      case 'delivered': case 'picked_up': return 3;
      case 'out_for_delivery': case 'attempted_delivery': case 'ready_for_pickup': return 2;
      case 'shipped': case 'in_transit': case 'delayed': case 'problem': return 1.5;
      case 'label_created': return 0.5;
    }
    return 0;
  }
  /* one package's headline (the pop-up says the state in a few words; r6: the timeline's headline says the same) */
  function packHead(f, opts) {
    var eta = futureEta(f, opts), dl = inst(f.deliveredAt);
    switch (f.state) {
      case 'delivered': return { text: dl ? 'Delivered ' + longDay(dl, opts) : 'Delivered', tab: dl ? 'Delivered ' + M.formatDate(dl, 'short', opts) : 'Delivered', tone: 'done' };
      case 'picked_up': return { text: dl ? 'Picked up ' + longDay(dl, opts) : 'Picked up', tab: 'Picked up', tone: 'done' };
      case 'out_for_delivery':
        var today = f.outForDeliveryAt && M.daysFromToday(inst(f.outForDeliveryAt), opts) === 0;
        return { text: today ? 'Out for delivery today' : 'Out for delivery', tab: 'Out for delivery', tone: 'live' };
      case 'shipped': case 'in_transit':
        return eta ? { text: 'On its way: arriving ' + relDay(eta, 'long', opts), tab: cap('arriving ' + relDay(eta, 'weekday', opts)), tone: 'live' }
          : { text: 'On its way', tab: 'On its way', tone: 'live' };
      case 'delayed': return { text: 'Delayed', tab: 'Delayed', tone: 'alert' };
      case 'attempted_delivery': return { text: 'Delivery attempted', tab: 'Delivery attempted', tone: 'alert' };
      case 'ready_for_pickup': return { text: 'Ready for pickup', tab: 'Ready for pickup', tone: 'live' };
      case 'problem': return { text: 'Delivery problem', tab: 'Delivery problem', tone: 'alert' };
      case 'label_created': return { text: 'Getting ready to ship', tab: 'Getting ready', tone: 'prep' };
    }
    return { text: 'Preparing your order', tab: 'Preparing', tone: 'prep' };
  }
  function popPack(f, o, opts) {
    var gone = packageGone(f), eta = futureEta(f, opts);
    var dl = inst(f.deliveredAt), ofd = inst(f.outForDeliveryAt), sh = inst(f.shippedAt), od = inst(o.processedAt);
    /* 2.2: the service's carrier; 2.1: a real parcel carrier among the tracking companies (CR-2) */
    var carrier = f.v22 ? f.carrier : uniq(f.tracking.map(function (x) { return carrierName(x.company); }))[0] || null;
    var short = function (d) { return d ? M.formatDate(d, 'short', opts) : ''; };
    return {
      head: packHead(f, opts), carrier: carrier && gone ? shippedWith([carrier]) : '', stage: stage(f.state),
      steps: [
        { label: 'Ordered', date: short(od) },
        { label: 'Shipped', date: gone ? short(sh) : '' },
        { label: 'Out for delivery', date: short(ofd) },
        { label: f.state === 'picked_up' ? 'Picked up' : 'Delivered', date: dl ? short(dl) : (eta ? 'Est. ' + M.formatDate(eta, 'short', opts) : '') }
      ],
      url: shipUrl(f), v22: !!f.v22, scans: Array.isArray(f.events),
      events: (f.events || []).map(function (e) { return eventRow(e, opts); }),
      /* the full 10 from /v1/orders: there may be more ("Show all") */
      maybeMore: Array.isArray(f.events) && f.events.length >= EVENTS_IN_ORDERS, full: false, more: false
    };
  }
  /* the normalized order -> the pop-up's model | null (nothing to show: no shipment) */
  function popModel(o, opts) {
    if (!o || !o.fulfillments.length) return null;
    var fs = o.fulfillments.slice(0, POP_PACKAGES_MAX);
    var packs = fs.map(function (f) { return popPack(f, o, opts); });
    var items = itemCount(o);
    var d = /^#([1-9][0-9]{0,11})$/.exec(o.name || '');
    var m = { order: o.name, digits: d ? d[1] : null, packs: packs, notes: [],
      eyebrow: 'Order ' + o.name + (items ? ' \u00b7 ' + items + ' ' + plural(items, 'item', 'items') : '') + (packs.length > 1 ? ' \u00b7 ' + packs.length + ' packages' : ''),
      /* "Show all" asks once for the whole order: only with a valid order number and a package that may have more */
      canShowAll: !!d && packs.some(function (p) { return p.maybeMore; }) };
    if (packs.length === 1) { m.title = packs[0].head.text; m.meta = packs[0].carrier; m.tone = packs[0].head.tone; }
    else {
      var done = packs.filter(function (p) { return p.stage === 3; }).length;
      m.title = done === packs.length ? 'Delivered in ' + packs.length + ' packages'
        : (done ? done + ' of ' + packs.length + ' packages delivered' : 'On its way in ' + packs.length + ' packages');
      /* review fix: the approved summary line under the title, one entry per package ("Arriving Fri, Oct 16 \u00b7 Delivered Oct 10" in the mockup) */
      m.meta = packs.map(function (p) { return p.head.tab; }).join(' \u00b7 ');
      m.tone = done === packs.length ? 'done' : (packs.some(function (p) { return p.head.tone === 'alert'; }) ? 'alert' : 'live');
    }
    return m;
  }
  /* a /v1/tracking answer applied to the pop-up's model (pure): matched packages get all their scans */
  function popApply(m, o, body, opts) {
    var merged = mergeTracking(o, body);
    if (!merged) return false;
    merged.slice(0, m.packs.length).forEach(function (f, i) {
      if (!f.full) return;
      var p = m.packs[i];
      if (f.events.length >= p.events.length) p.events = f.events.map(function (e) { return eventRow(e, opts); });
      p.full = true; p.more = f.moreScans;
      if (f.carrier && packageGone(f)) p.carrier = shippedWith([f.carrier]);
    });
    if (body.moreShipments === true && m.notes.indexOf(COPY_TRACK.moreShipmentsPop) < 0) m.notes.push(COPY_TRACK.moreShipmentsPop);
    m.meta = m.packs.length === 1 ? m.packs[0].carrier : m.packs.map(function (p) { return p.head.tab; }).join(' \u00b7 ');
    return true;
  }

  /* input: {orders, sub, subs (list|null), loyalty, badge, contact, now} -> model | null
     why (r7, optional): an object; on null, why.reason = 'no-sub' | 'no-matching-order' | 'no-headline' */
  function timelineModel(input, why) {
    why = why || {};
    var sub = input && input.sub;
    if (!sub || !Array.isArray(input.orders)) { why.reason = 'no-sub'; return null; }
    var now = typeof input.now === 'number' ? input.now : Date.now();
    var opts = { now: now };
    var loyalty = input.loyalty || {};
    var tz = loyalty.tz || null;
    var subs = Array.isArray(input.subs) ? input.subs : null;
    var orders = input.orders.filter(function (o) { return o.status !== 'canceled'; });
    var pick = ordersForSub(orders, sub, subs ? subs.length : null);
    if (!pick) { why.reason = 'no-matching-order'; return null; }
    var o = pick.orders[0], f = primary(o);
    var head = headline(o, f, opts);
    if (!head) { why.reason = 'no-headline'; return null; }
    var count = itemCount(o);
    /* "Latest order" only when it really is the customer's newest order; when a newer order (a
       one-off, another subscription) exists, the home banner shows that one, so say whose it is */
    var newer = pick.how === 'matched' && orders[0] && orders[0].name !== o.name;
    var model = {
      sub: sub.id, how: pick.how, order: o.name, status: o.status, tone: head.tone,
      eyebrow: newer ? 'Latest order for this subscription' : 'Latest order',
      headline: head.text, headlineContact: head.contact ? input.contact || null : null, headlineContactText: head.contact || null,
      meta: 'Order ' + o.name + (count ? ' \u00b7 ' + count + ' ' + plural(count, 'item', 'items') : ''),
      ordersUrl: ORDERS_PATH,
      flag: o.partiallyShipped ? 'Part of this order has shipped' : (o.fulfillments.length > 1 ? 'Shipping in ' + o.fulfillments.length + ' packages' : null),
      steps: steps(o, f, sub, tz, opts), contact: input.contact || null,
      /* "Track package" under the steps opens the pop-up for this order (option B: no list of its own) */
      track: canTrack(o) ? o.name : null
    };
    /* schedule */
    var hist = history(orders, opts);
    var streakOk = SHOW_STREAK && sub.full && sub.streak !== null && sub.streak >= 0 && input.badge !== null && input.badge === sub.streak;
    var openCount = subs ? subs.filter(function (s) { return /^(ACTIVE|PAUSED|FAILED)$/.test(s.status || ''); }).length : null;
    var cur = loyalty.ladder && loyalty.current ? loyalty.ladder.filter(function (t) { return t.key === loyalty.current; })[0] : null;
    var curIdx = cur ? loyalty.ladder.indexOf(cur) : -1;
    var nextT = curIdx >= 0 ? loyalty.ladder[curIdx + 1] : null;
    var milestonesOk = SHOW_TIER_MILESTONES && streakOk && openCount === 1 && openSub(sub) && !!cur &&
      (subs || []).some(function (s) { return s.id === sub.id; }) &&
      sub.streak >= cur.min && (!nextT || sub.streak < nextT.min);
    var rows = upcoming(sub, tz).map(function (u) {
      var titles = u.lines.map(function (l) { return l.title + (l.quantity > 1 ? ' \u00d7' + l.quantity : ''); });
      var row = {
        date: M.formatDate(u.ymd, 'weekday', opts), when: u.k === 1 ? 'Next order' : (streakOk ? 'Would be ' + (sub.streak + u.k) + ' in a row' : ''),
        what: titles.length ? itemsText(titles, 3) : 'No products scheduled',
        arrives: hist.orderToDoor !== null ? 'Arrives about ' + M.formatDate(ymdOf(M.addDays(u.ymd, hist.orderToDoor)), 'short', opts) : '',
        tier: null
      };
      if (milestonesOk) {
        var reach = loyalty.ladder.filter(function (t) { return t.min > cur.min && t.min === sub.streak + u.k; })[0];
        if (reach) row.tier = { name: reach.name, key: reach.key, art: M.tierArt(reach.key) };
      }
      return row;
    });
    var delivered = pick.orders.filter(function (x) { return x.status === 'delivered' || x.status === 'picked_up'; }).slice(0, 5).map(function (x) {
      var g = primary(x), tn = trackingNote(x);
      /* "Track package" opens the pop-up for that order (the one tracking action) */
      var days = g && g.shippedAt && g.deliveredAt ? M.daysBetween(inst(g.shippedAt), inst(g.deliveredAt), opts) : null;
      return {
        date: M.formatDate(inst(x.processedAt), 'weekday', opts), order: x.name,
        what: (x.status === 'picked_up' ? 'Picked up ' : 'Delivered ') + (g && g.deliveredAt ? M.formatDate(inst(g.deliveredAt), 'weekday', opts) : ''),
        days: days === null || days < 0 ? '' : (days === 0 ? 'The day it shipped' : days + ' ' + plural(days, 'day', 'days') + ' after it shipped'),
        carrier: tn.carrierText, track: canTrack(x) ? x.name : null
      };
    });
    var label = scheduleLabel(sub.interval, sub.count);
    var foot = [];
    if (rows.length > 1 && label) foot.push('Dates follow your schedule (' + label + '). Skipping an order or changing how often it ships updates this list.');
    else if (rows.length) foot.push('Skipping an order or changing its date updates this list.');
    if (rows.length && hist.orderToDoor !== null) foot.push('Arrival dates are estimates from your past deliveries.');
    if (rows.some(function (r) { return r.tier || /in a row/.test(r.when); })) foot.push('Counts assume each order ships on schedule.');
    foot.push('Shows orders from the last 60 days.');
    model.schedule = (rows.length || delivered.length) ? {
      coming: rows, delivered: delivered, foot: foot.join(' '),
      usual: hist.shipMin === null ? '' : (hist.shipMin === hist.shipMax ? 'Usually arrives about ' + hist.shipMin + ' ' + plural(hist.shipMin, 'day', 'days') + ' after it ships'
        : 'Usually arrives ' + hist.shipMin + ' to ' + hist.shipMax + ' days after it ships')
    } : null;
    return model;
  }

  /* input: {orders, contact, now} -> model | null (newest order only) */
  function bannerModel(input) {
    var orders = input && Array.isArray(input.orders) ? input.orders : [];
    var o = orders[0];
    if (!o) return null;
    var now = typeof input.now === 'number' ? input.now : Date.now();
    var opts = { now: now };
    var f = primary(o), eta = futureEta(f, opts), st = o.status;
    var verb, tone = 'live', icon = 'truck', lead = null;
    if (st === 'processing' || st === 'label_created') {
      if ((now - ms(o.processedAt)) / DAY_MS > BANNER_PREPARING_DAYS) return null;
      verb = 'is being prepared'; tone = 'prep'; icon = 'box';
    } else if (st === 'delivered') {
      var d = f && ms(f.deliveredAt);
      if (!d || now - d > BANNER_DELIVERED_MS || d - now > 3600000) return null;
      verb = 'was delivered'; tone = 'done'; icon = 'check';
      lead = 'Delivered ' + relDay(inst(f.deliveredAt), 'weekday', opts);
    } else if (st === 'shipped' || st === 'in_transit') verb = 'is on its way';
    else if (st === 'out_for_delivery') { verb = 'is out for delivery'; if (f && f.outForDeliveryAt && M.daysFromToday(inst(f.outForDeliveryAt), opts) === 0) lead = 'Arriving today'; }
    else if (st === 'delayed') { verb = 'is delayed'; tone = 'alert'; icon = 'alert'; }
    else if (st === 'attempted_delivery') { verb = 'had a delivery attempt'; tone = 'alert'; icon = 'alert'; }
    else if (st === 'problem') { verb = 'has a delivery problem'; tone = 'alert'; icon = 'alert'; }
    else if (st === 'ready_for_pickup') verb = 'is ready for pickup';
    else return null;   /* canceled, picked up */
    if (!lead && eta && st !== 'delivered') lead = cap('arriving ' + relDay(eta, 'weekday', opts));
    var tn = trackingNote(o);
    /* "Shipped with <carrier>" claims the order has shipped: never while it is still being prepared (the
       service 2.2 can know the carrier from the tracking company before the first scan) */
    var sub = [lead, shipped(st) ? tn.carrierText : '', itemsText(o.items.map(function (i) { return i.title; }), 3)].filter(function (x) { return x; });
    var actions = [];
    /* ONE tracking action: "Track package(s)" opens the pop-up for this order (batch 3) */
    if (canTrack(o)) actions.push({ text: o.fulfillments.length > 1 ? COPY_TRACK.openMany : COPY_TRACK.open, track: true, order: o.name });
    if (st === 'problem' && input.contact) actions.unshift({ text: 'Contact us', href: input.contact, external: false });
    return {
      order: o.name, status: st, tone: tone, icon: icon,
      title: 'Your ' + M.formatDate(inst(o.processedAt), 'short', opts) + ' order ' + verb,
      sub: sub.join(' \u00b7 '), subParts: sub, actions: actions.slice(0, 3)
    };
  }

  /* input: {orders, contact, now} -> {'#28761': {text, tone, contact, contactText, flag, carrier, numbers, track}} for the orders page */
  function ordersPageModel(input) {
    var orders = input && Array.isArray(input.orders) ? input.orders : [];
    var now = input && typeof input.now === 'number' ? input.now : Date.now();
    var opts = { now: now }, out = {};
    orders.forEach(function (o) {
      if (!o || o.status === 'canceled' || !/^#\d{1,12}$/.test(o.name || '')) return;
      var f = primary(o), head = headline(o, f, opts);
      if (!head) return;
      var tn = trackingNote(o), track = canTrack(o);
      out[o.name] = {
        text: head.text, tone: head.tone,
        contact: head.contact ? (input.contact || null) : null, contactText: head.contact || null,
        flag: o.partiallyShipped ? 'Part of this order has shipped' : null,
        carrier: shipped(o.status) ? tn.carrierText : '',
        /* "Track package" opens the pop-up; without scans or a tracking page the tracking number stays as text */
        track: track, numbers: track ? [] : tn.numbers.slice(0, 2)
      };
    });
    return out;
  }
  /* "Order #28761" (Skio's card header) -> "#28761"; anything else -> null */
  function cardOrderName(card) {
    var h = M.byName('order-card-header-number', card);
    var m = h ? /^\s*order\s*#\s*(\d{1,12})\s*$/i.exec(h.textContent || '') : null;
    return m ? '#' + m[1] : null;
  }

  /* ------------------------------------------------------------------ */
  /* DOM                                                                  */
  /* ------------------------------------------------------------------ */
  var ICONS = {
    check: ['M5 12.5l4.5 4.5L19 7.5'],
    calendar: ['M6 5h12a2.5 2.5 0 0 1 2.5 2.5v10a2.5 2.5 0 0 1-2.5 2.5H6a2.5 2.5 0 0 1-2.5-2.5v-10A2.5 2.5 0 0 1 6 5z', 'M3.5 10h17', 'M8 3v4', 'M16 3v4'],
    truck: ['M3 7h11v9H3z', 'M14 10h4l3 3v3h-7', 'M8.8 17.5a1.8 1.8 0 1 1-3.6 0a1.8 1.8 0 1 1 3.6 0z', 'M18.8 17.5a1.8 1.8 0 1 1-3.6 0a1.8 1.8 0 1 1 3.6 0z'],
    box: ['M3.5 7.5L12 3l8.5 4.5v9L12 21l-8.5-4.5z', 'M3.5 7.5L12 12l8.5-4.5', 'M12 12v9'],
    alert: ['M12 3.5l9 15.5H3z', 'M12 10v4', 'M12 16.8v.2'],
    chevron: ['M6 9l6 6 6-6'],
    ext: ['M14 4h6v6', 'M20 4l-9 9', 'M18 14v4.5A1.5 1.5 0 0 1 16.5 20h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6H10']
  };
  function icon(name, cls, width) {
    var s = M.svg('0 0 24 24', ICONS[name] || [], { 'class': cls || '' });
    if (width) s.setAttribute('stroke-width', String(width));
    return s;
  }
  function link(text, href, external, cls) {
    var u = M.safeUrl(href, external ? { absolute: true } : { sameOrigin: true });
    if (!u) return null;
    return M.el('a', external ? { href: u, target: '_blank', rel: 'noopener noreferrer', 'class': cls || null, 'aria-label': text + ' (opens in a new tab)' } : { href: u, 'class': cls || null }, text);
  }
  /* the one small external link: "View on the tracking site" (https only, new tab) */
  function carrierLink(href, cls) {
    var u = M.safeUrl(href, { absolute: true, httpsOnly: true });
    if (!u) return null;
    return M.el('a', { href: u, target: '_blank', rel: 'noopener noreferrer', 'class': cls },
      COPY_TRACK.carrierLink, M.el('span', { 'class': 'mk-sr mk-dlg-sr' }, COPY_TRACK.newTab), icon('ext', 'trk-ext', 2));
  }
  /* "Track package": a button that opens the pop-up for one order (data-mk-track = its name, to find it again) */
  function trackButton(cls, text, order) {
    if (!POP || typeof order !== 'string') return null;
    var b = M.el('button', { type: 'button', 'class': cls, 'aria-haspopup': 'dialog', 'data-mk-track': order }, text);
    b.addEventListener('click', function (ev) { M.guard(openTrack)(order, ev.currentTarget); });
    return b;
  }
  /* "USPS . Track package": short parts (and links) never break inside, and the dot stays at
     the end of a line, so narrow step columns wrap between parts, not mid-phrase */
  var SEG_MAX = 24;
  function joinDots(parts) {
    var items = parts.filter(function (p) { return p; }), out = [];
    items.forEach(function (p, i) {
      var last = i === items.length - 1;
      var kids = [p];
      if (!last) kids.push(M.el('span', { 'class': 'mk-dot', 'aria-hidden': 'true' }, ' \u00b7'));
      if (typeof p !== 'string' || p.length <= SEG_MAX) out.push(M.el('span', { 'class': 'tl-seg' }, kids));
      else out = out.concat(kids);
      if (!last) out.push(' ');
    });
    return out;
  }
  function buildStep(s) {
    var dotIcon = s.state === 'done' ? icon('check', '', 3) : (s.state === 'next' ? icon('calendar', '', 2) : (s.state === 'alert' ? icon('alert', '', 2.2) : null));
    var noteParts = (s.note || []).slice();
    if (s.contact) noteParts.push(link('Contact us', CTX.contact, false));
    var stateLabel = { done: 'Done', current: 'In progress', alert: 'Needs attention', upcoming: 'Not yet', next: 'Upcoming' }[s.state] || '';
    return M.el('li', { 'class': 'tl-step is-' + s.state, 'data-step': s.key },
      M.el('span', { 'class': 'tl-dot', 'aria-hidden': 'true' }, dotIcon),
      M.el('span', { 'class': 'tl-label' }, s.label, M.el('span', { 'class': 'mk-sr' }, ' (' + stateLabel + ')')),
      s.date ? M.el('span', { 'class': 'tl-date' }, s.date) : null,
      noteParts.filter(Boolean).length ? M.el('span', { 'class': 'tl-note' }, joinDots(noteParts.filter(Boolean))) : null);
  }
  function buildSchedule(sc, open) {
    var groups = [];
    if (sc.coming.length) {
      groups.push(M.el('section', { 'class': 'tl-group', 'aria-label': 'Coming up' },
        M.el('h4', { 'class': 'tl-group-title' }, 'Coming up'),
        M.el('ol', { 'class': 'tl-list' }, sc.coming.map(function (r, i) {
          var tag = r.tier ? M.el('span', { 'class': 'tl-tag tl-tag--tier' },
            r.tier.art ? M.el('img', { src: r.tier.art, alt: '', width: '20', height: '20', loading: 'lazy', decoding: 'async' }) : null,
            'Unlocks ' + r.tier.name) : null;
          return M.el('li', { 'class': 'tl-row' + (i === 0 ? ' is-next-row' : '') },
            M.el('span', { 'class': 'tl-when' }, M.el('b', null, r.date), r.when ? M.el('small', null, r.when) : null),
            M.el('span', { 'class': 'tl-what' }, M.el('b', null, r.what), r.arrives ? M.el('small', null, r.arrives) : null),
            tag);
        }))));
    }
    if (sc.delivered.length) {
      groups.push(M.el('section', { 'class': 'tl-group', 'aria-label': 'Delivered' },
        M.el('h4', { 'class': 'tl-group-title' }, 'Delivered'),
        M.el('ol', { 'class': 'tl-list' }, sc.delivered.map(function (r) {
          var small = joinDots([r.days, r.carrier, r.track ? trackButton('tl-track-btn', COPY_TRACK.open, r.track) : null, link('View order', ORDERS_PATH, false)]);
          return M.el('li', { 'class': 'tl-row is-past' },
            M.el('span', { 'class': 'tl-when' }, M.el('b', null, r.date), M.el('small', null, r.order)),
            M.el('span', { 'class': 'tl-what' }, M.el('b', null, r.what), small.length ? M.el('small', null, small) : null));
        }))));
    }
    groups.push(M.el('p', { 'class': 'tl-foot' }, sc.foot));
    var panel = M.el('div', { id: IDS.schedule, 'class': 'tl-schedule', role: 'region', 'aria-label': 'Delivery schedule' }, groups);
    if (!open) panel.hidden = true;
    return panel;
  }
  function buildTimeline(m) {
    var open = !!S.expanded[m.sub];
    var titleKids = [m.headline];
    if (m.headlineContactText) {
      var c = m.headlineContact ? link(m.headlineContactText, m.headlineContact, false) : null;
      titleKids.push(c || m.headlineContactText);
    }
    var kids = [
      M.el('header', { 'class': 'tl-head' },
        M.el('div', { 'class': 'tl-head-main' },
          M.el('p', { 'class': 'tl-eyebrow' }, m.eyebrow),
          M.el('h3', { id: IDS.title, 'class': 'tl-title' }, titleKids)),
        M.el('p', { 'class': 'tl-order' }, m.meta, ' ', link('View order', m.ordersUrl, false, 'tl-link'))),
      m.flag ? M.el('p', { 'class': 'tl-flag' }, m.flag) : null,
      M.el('ol', { 'class': 'tl-steps', 'data-count': String(m.steps.length) }, m.steps.map(buildStep))
    ];
    /* one row (reading and tab order): "Track package" (the pop-up for this order, option B), then "Show delivery
       schedule", its note and panel; the closed panel takes no room, so the two buttons sit side by side on wide screens */
    var more = [];
    var tb = m.track ? trackButton('tl-track-open', COPY_TRACK.open, m.track) : null;
    if (tb) more.push(tb);
    if (m.schedule) {
      var btn = M.el('button', { type: 'button', 'class': 'tl-toggle', 'aria-expanded': open ? 'true' : 'false', 'aria-controls': IDS.schedule },
        M.el('span', { 'class': 'tl-toggle-text' }, open ? 'Hide delivery schedule' : 'Show delivery schedule'), icon('chevron', 'tl-chev', 2.4));
      var panel = buildSchedule(m.schedule, open);
      btn.addEventListener('click', function () {
        try {
          var now = btn.getAttribute('aria-expanded') !== 'true';
          btn.setAttribute('aria-expanded', now ? 'true' : 'false');
          M.text(btn.querySelector('.tl-toggle-text'), now ? 'Hide delivery schedule' : 'Show delivery schedule');
          panel.hidden = !now;
          S.expanded[m.sub] = now;
        } catch (e) { /* ignore */ }
      });
      more.push(btn, m.schedule.usual ? M.el('span', { 'class': 'tl-more-note' }, m.schedule.usual) : null, panel);
    }
    if (more.length) kids.push(M.el('div', { 'class': 'tl-more' }, more));
    return M.el('section', { 'class': 'mk-tl tone-' + m.tone, 'aria-labelledby': IDS.title, 'data-status': m.status }, kids);
  }

  function buildOrderLine(m) {
    var head = [m.text];
    if (m.contactText) {
      var c = m.contactText && m.contact ? link(m.contactText, m.contact, false) : null;
      head.push(c || m.contactText);
    }
    var parts = [m.carrier].concat(m.numbers || []);
    if (m.track) parts.push(trackButton('oc-track', COPY_TRACK.open, m.order));
    parts = parts.filter(Boolean);
    return M.el('div', { 'class': 'mk-oc tone-' + m.tone },
      M.el('span', { 'class': 'oc-dot', 'aria-hidden': 'true' }),
      M.el('p', { 'class': 'oc-text' }, M.el('b', null, head), m.flag ? M.el('span', { 'class': 'oc-flag' }, ' (' + m.flag.toLowerCase() + ')') : null),
      parts.length ? M.el('p', { 'class': 'oc-links' }, joinDots(parts)) : null);
  }
  function buildBanner(m) {
    var acts = m.actions.map(function (a) { return a.track ? trackButton('db-btn', a.text, a.order) : link(a.text, a.href, a.external, 'db-btn'); }).filter(Boolean);
    return M.el('section', { 'class': 'mk-db tone-' + m.tone, 'aria-label': 'Latest delivery', 'data-status': m.status },
      M.el('span', { 'class': 'db-ico', 'aria-hidden': 'true' }, icon(m.icon, '', 2)),
      M.el('div', { 'class': 'db-text' },
        M.el('p', { 'class': 'db-title' }, m.title),
        /* parts stay whole and the dot stays at the end of a line (no line starting with a dot) */
        m.sub ? M.el('p', { 'class': 'db-sub' }, Array.isArray(m.subParts) ? joinDots(m.subParts) : m.sub) : null),
      acts.length ? M.el('div', { 'class': 'db-actions' }, acts) : null);
  }

  /* ------------------------------------------------------------------ */
  /* Track package pop-up (MKPortal.dialog: one dialog at a time, focus  */
  /* trap, Escape / scrim / X, focus back to the opener, closed on a     */
  /* route change; centred on desktop / tablet, bottom sheet on phones). */
  /* The tablist, the scroll region (the dialog body, 'Tracking details') */
  /* and one polite live region are built once per open; a state change  */
  /* redraws only the panel and the footer link and puts focus back on   */
  /* what had it ("Show all" / "Try again", which turn into the spinner, */
  /* hand focus to the scroll region).                                   */
  /* ------------------------------------------------------------------ */
  var P = null;   /* {h, o, m, tab, fetch: 'idle'|'loading'|'done'|'error'|'not_found', seq} while open */
  var popSeq = 0;
  /* the normalized order from the data this page already has (home banner, orders page, subscription page) */
  function findOrder(name) {
    var lists = [S.home && S.home.data && S.home.data.orders, S.orders && S.orders.data && S.orders.data.orders,
      S.detail && S.detail.data && S.detail.data.orders];
    for (var i = 0; i < lists.length; i++) {
      var l = Array.isArray(lists[i]) ? lists[i] : [];
      for (var j = 0; j < l.length; j++) if (l[j] && l[j].name === name) return l[j];
    }
    return null;
  }
  /* the Track package button of this order on the page now (a redraw may have replaced the opener) */
  function trackOpenerOf(name) {
    var bs = M.qa('[data-mk-track]');
    for (var i = 0; i < bs.length; i++) if (bs[i].getAttribute('data-mk-track') === name && bs[i].isConnected) return bs[i];
    return null;
  }
  function popSteps(p) {
    var fill = Math.max(0, Math.min(1, p.stage / 3));
    return M.el('div', { 'class': 'trk-progress' },
      M.el('div', { 'class': 'trk-bar', 'aria-hidden': 'true' }, M.el('i', { style: { width: (fill * 100).toFixed(1) + '%' } })),
      M.el('ol', { 'class': 'trk-steps', 'aria-label': 'Progress' }, p.steps.map(function (s, i) {
        var done = i <= p.stage;
        return M.el('li', { 'class': 'trk-step is-' + (done ? 'done' : 'todo') },
          M.el('span', { 'class': 'trk-step-dot', 'aria-hidden': 'true' }, done ? icon('check', '', 3) : null),
          M.el('span', { 'class': 'trk-step-label' }, s.label, M.el('span', { 'class': 'mk-dlg-sr' }, done ? ' (done)' : ' (not yet)')),
          s.date ? M.el('span', { 'class': 'trk-step-date' }, s.date) : null);
      })));
  }
  function popEvents(p) {
    if (!p.events.length) return M.el('p', { 'class': 'trk-note' }, COPY_TRACK.none);
    var groups = [], cur = null;
    p.events.forEach(function (e) {
      if (!cur || cur.k !== e.day) { cur = { k: e.day, head: e.dayHead, rows: [] }; groups.push(cur); }
      cur.rows.push(e);
    });
    var first = true;
    /* only the label so far: say when real scans will appear (no time promise) */
    var labelOnly = p.events.every(function (e) { return e.words === 'Label created'; });
    return M.el('div', { 'class': 'trk-days' }, groups.map(function (g) {
      return M.el('section', { 'class': 'trk-day', 'aria-label': g.head },
        M.el('h4', { 'class': 'trk-day-head' }, g.head),
        M.el('ol', { 'class': 'trk-events' }, g.rows.map(function (e) {
          /* r6: the main line is the carrier's note (or the status words); a tag row (.trk-ev-sub: tag + place) only when
             the status adds something, so a row without a tag (every row of a 2.2 answer) is built exactly as in 1.1.1 */
          var where = e.where ? M.el('span', { 'class': 'trk-ev-where' }, e.where) : null;
          var li = M.el('li', { 'class': 'trk-ev' + (first ? ' is-latest' : '') },
            M.el('span', { 'class': 'trk-ev-dot', 'aria-hidden': 'true' }),
            M.el('span', { 'class': 'trk-ev-what' }, e.text, first ? M.el('span', { 'class': 'mk-dlg-sr' }, ' (latest)') : null),
            M.el('time', { 'class': 'trk-ev-time', datetime: e.at }, e.time),
            e.tag ? M.el('span', { 'class': 'trk-ev-sub' }, M.el('span', { 'class': 'trk-ev-tag' + (e.alert ? ' tone-alert' : '') }, e.tag), where) : where);
          first = false;
          return li;
        })));
    }), labelOnly ? M.el('p', { 'class': 'trk-note' }, COPY_TRACK.none) : null);
  }
  /* under the scans: "Show all" / loading / error + Try again / not available; the notes */
  function popState(p) {
    var box = M.el('div', { 'class': 'trk-state' }), m = P.m;
    if (p.maybeMore && !p.full && m.canShowAll) {
      if (P.fetch === 'idle') {
        var all = M.el('button', { type: 'button', 'class': 'trk-all' }, COPY_TRACK.all);
        all.addEventListener('click', function () { M.guard(popFetch)(false); });
        box.appendChild(all);
      } else if (P.fetch === 'loading') {
        box.appendChild(M.el('p', { 'class': 'trk-loading' }, M.el('span', { 'class': 'trk-spin', 'aria-hidden': 'true' }), COPY_TRACK.loadingStatus));
      } else if (P.fetch === 'error') {
        var b = M.el('button', { type: 'button', 'class': 'trk-retry' }, COPY_TRACK.retry);
        b.addEventListener('click', function () { M.guard(popFetch)(true); });
        box.appendChild(M.el('p', { 'class': 'trk-msg is-error' }, icon('alert', 'trk-msg-ico', 2.2), M.el('span', null, COPY_TRACK.error, ' '), b));
      } else {
        /* 404, or an answer without this package */
        box.appendChild(M.el('p', { 'class': 'trk-msg' }, COPY_TRACK.notFound + (p.url ? COPY_TRACK.notFoundLink : '')));
      }
    }
    if (p.more) box.appendChild(M.el('p', { 'class': 'trk-msg' }, COPY_TRACK.moreScans));
    m.notes.forEach(function (n) { box.appendChild(M.el('p', { 'class': 'trk-msg' }, n)); });
    return box;
  }
  function popPanel(p, i) {
    var multi = P.m.packs.length > 1;
    return M.el('div', multi ? { 'class': 'trk-panel', role: 'tabpanel', id: IDS.pop + '-panel-' + i, 'aria-labelledby': IDS.pop + '-tab-' + i } : { 'class': 'trk-panel' },
      multi ? M.el('p', { 'class': 'trk-pack-head tone-' + p.head.tone }, M.el('b', null, p.head.text), p.carrier ? [' ', M.el('span', { 'class': 'trk-nw' }, '\u00b7 ' + p.carrier)] : null) : null,
      popSteps(p),
      /* service 2.1 (no scans at all): the status and the carrier link only */
      p.scans ? M.el('div', { 'class': 'trk-sec' }, M.el('h3', { 'class': 'trk-sec-title' }, COPY_TRACK.title), M.el('span', { 'class': 'trk-sec-aside' }, COPY_TRACK.newest)) : null,
      p.scans ? popEvents(p) : null,
      popState(p));
  }
  /* built once per open: a tab switch only updates aria-selected / tabindex, so the focused tab is never replaced */
  function popTabs(m) {
    var list = M.el('div', { 'class': 'trk-tabs', role: 'tablist', 'aria-label': COPY_TRACK.packages }, m.packs.map(function (p, i) {
      var t = M.el('button', { type: 'button', role: 'tab', id: IDS.pop + '-tab-' + i, 'class': 'trk-tab tone-' + p.head.tone,
        'aria-selected': i === P.tab ? 'true' : 'false', 'aria-controls': IDS.pop + '-panel-' + i, tabindex: i === P.tab ? '0' : '-1' },
        M.el('span', { 'class': 'trk-tab-name' }, 'Package ' + (i + 1)), M.el('span', { 'class': 'trk-tab-sub' }, p.head.tab));
      t.addEventListener('click', function () { popSelect(i, false); });
      return t;
    }));
    list.addEventListener('keydown', function (ev) {
      var n = m.packs.length, k = ev.key;
      if (k !== 'ArrowRight' && k !== 'ArrowLeft' && k !== 'Home' && k !== 'End') return;
      ev.preventDefault();
      popSelect(k === 'Home' ? 0 : k === 'End' ? n - 1 : (P.tab + (k === 'ArrowRight' ? 1 : n - 1)) % n, true);
    });
    return list;
  }
  function popSelect(i, moveFocus) {
    if (!P || !P.h.isOpen()) return;
    P.tab = i;
    M.qa('.trk-tab', P.h.root).forEach(function (t, j) {
      t.setAttribute('aria-selected', j === i ? 'true' : 'false'); t.setAttribute('tabindex', j === i ? '0' : '-1');
    });
    popDraw('tab');
    if (moveFocus) popFocus(document.getElementById(IDS.pop + '-tab-' + i));
  }
  function popFocus(t) {
    if (!t) return;
    try { t.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
  }
  /* which redrawn element has focus, to find it again after the redraw */
  function popFocusKey() {
    var a = document.activeElement;
    if (!a || !P || !P.h.root.contains(a)) return null;
    if (a.classList.contains('trk-all') || a.classList.contains('trk-retry')) return 'action';
    if (a.classList.contains('trk-carrier')) return 'link';
    return null;   /* title, X, tabs, the scroll region: never replaced */
  }
  function popRestore(k) {
    if (!k || !P) return;
    var r = P.h.root;
    var t = k === 'link' ? r.querySelector('.trk-carrier') : r.querySelector('.trk-all, .trk-retry');
    /* "Show all" / "Try again" became the spinner (or the link went away): the scroll region */
    popFocus(t || P.h.body);
  }
  /* one live region, built once and never replaced, so screen readers hear each change */
  function popAnnounce(text) {
    var r = P && P.h.root.querySelector('.trk-live');
    if (!r) return;
    r.textContent = '';
    if (text) setTimeout(function () { if (r.isConnected) r.textContent = text; }, 60);
  }
  /* why: 'open' (first draw), 'tab' (another package: from the top), 'state' (a fetch result: keep the scroll) */
  function popDraw(why) {
    if (!P || !P.h.isOpen()) return;
    var m = P.m, h = P.h, body = h.body, slot = h.root.querySelector('.trk-panel-slot');
    if (!slot) return;
    var y = why === 'state' ? body.scrollTop : 0, k = popFocusKey();
    var p = m.packs[Math.min(P.tab, m.packs.length - 1)];
    slot.textContent = '';
    slot.appendChild(popPanel(p, P.tab));
    var l = p.url ? carrierLink(p.url, 'trk-carrier') : null;
    h.set({ actions: l ? M.el('p', { 'class': 'trk-foot' }, l) : null, focus: false });
    body.scrollTop = y;
    body.setAttribute('aria-busy', P.fetch === 'loading' ? 'true' : 'false');
    popRestore(k);
  }
  /* "Show all" / "Try again": GET /v1/tracking for this order (MKPortal.tracking), once per click */
  function popFetch(retry) {
    if (!P || !P.h.isOpen() || !P.m.canShowAll || P.fetch === 'loading') return;
    var mine = P;
    mine.fetch = 'loading';
    popDraw('state');
    popAnnounce(COPY_TRACK.loadingStatus);
    Promise.resolve(M.tracking(mine.o.name, retry ? { fresh: true } : undefined)).then(null, function () { return null; }).then(function (r) {
      if (P !== mine || !mine.h.isOpen()) return;
      if (r && r.ok && popApply(mine.m, mine.o, r.data, { now: Date.now() })) mine.fetch = 'done';
      else mine.fetch = r && (r.reason === 'not_found' || r.ok) ? 'not_found' : 'error';
      popDraw('state');
      var p = mine.m.packs[Math.min(mine.tab, mine.m.packs.length - 1)];
      popAnnounce(mine.fetch === 'done' ? (p.full || !p.maybeMore ? COPY_TRACK.loaded : COPY_TRACK.notFound)
        : mine.fetch === 'not_found' ? COPY_TRACK.notFound : COPY_TRACK.error);
    });
  }
  /* open the pop-up for one order (name '#28761'); opener: the button pressed (ev.currentTarget) */
  function openTrack(name, opener) {
    if (!POP || !M.on()) return null;
    var o = findOrder(name);
    var m = o ? popModel(o, { now: Date.now() }) : null;
    if (!m) return null;
    var mine = { o: o, m: m, tab: 0, fetch: 'idle', seq: ++popSeq, h: null };
    var prev = P;
    P = mine;
    var tabs = m.packs.length > 1 ? popTabs(m) : null;
    var wrap = M.el('div', { 'class': 'trk-wrap' }, tabs, M.el('div', { 'class': 'trk-panel-slot' }),
      M.el('p', { 'class': 'trk-live mk-dlg-sr', role: 'status', 'aria-live': 'polite', 'aria-atomic': 'true' }));
    var h = M.dialog.open({
      id: IDS.pop, title: m.title, module: 'delivery', className: 'mk-trk tone-' + m.tone,
      icon: M.el('p', { 'class': 'trk-eyebrow' }, m.eyebrow),
      body: wrap, opener: opener || null, wide: true, handle: false, bodyLabel: COPY_TRACK.region,
      returnFocus: function () { return trackOpenerOf(name); },
      onClose: function () { if (P === mine) P = null; }
    });
    if (!h) { if (P === mine) P = prev && prev.h && prev.h.isOpen() ? prev : null; return null; }
    mine.h = h;
    /* "Shipped with USPS" under the headline (the header stays put; only the body scrolls) */
    if (m.meta && h.title && h.title.parentNode) {
      /* the truck only beside a carrier line (one package), as in the approved design */
      h.title.parentNode.insertBefore(M.el('p', { 'class': 'trk-meta' }, m.packs.length === 1 ? icon('truck', 'trk-meta-ico', 1.8) : null, m.meta), h.title.nextSibling);
    }
    popDraw('open');
    h.focusTitle();
    return h;
  }

  /* ------------------------------------------------------------------ */
  /* lifecycle                                                            */
  /* ------------------------------------------------------------------ */
  var S = { detail: null, home: null, orders: null, expanded: {}, timer: null, obs: null, refreshAt: 0, ocIds: [] };
  var CTX = { contact: null };

  /* r7 (E1): why a read gave no card. Retryable: the service or a Skio query failed (the answer may come next
     time); not: a real answer (no orders). delivery-failed:<status> carries the core's code only (429, 503,
     'timeout' ...: MKPortal.deliveryStatus), never a body */
  function Fail(reason) { this.reason = reason; }
  function deliveryFail() {
    var st = typeof M.deliveryStatus === 'function' ? M.deliveryStatus() : null;
    return new Fail('delivery-failed:' + (st && st.status != null ? st.status : 'unknown'));
  }
  function retryable(why) { return /^(delivery-failed|user-id|sub-query|error)/.test(why || ''); }
  /* run() again later, while the route is the same, the tab visible and fewer than MAX_RETRIES tries were made */
  function retryLater(my, why, run) {
    if (!retryable(why) || my.tries >= MAX_RETRIES) return;
    var wait = why === 'delivery-failed:429' ? RETRY_429_MS : RETRY_MS;
    my.retryAt = Date.now() + wait;
    function go() {
      if (M.route().seq !== my.seq) { my.retryAt = 0; return; }
      if (document.hidden) {
        document.addEventListener('visibilitychange', function v() {
          if (document.hidden) return;
          document.removeEventListener('visibilitychange', v);
          go();
        });
        return;
      }
      my.retryAt = 0;
      run();
    }
    setTimeout(go, wait);
  }
  function guarded(fn, args) {
    var p = M.guard(fn).apply(null, args);
    return p && typeof p.then === 'function' ? p : Promise.resolve(null);
  }
  /* keepOrders (r7 review fix): the orders of the card already shown on this route. When the delivery read fails they
     stand in for its answer, but Skio's subscription is still read (fresh), so the Next order date and the Coming up
     list never come from an old answer; the result then carries deliveryFailed (the reason) for a retry */
  function loadDetail(id, fresh, keepOrders) {
    var o = { fresh: !!fresh };
    return Promise.all([M.delivery(DETAIL_LIMIT, o), M.storefrontUserId()]).then(function (r) {
      var dFail = r[0] ? null : deliveryFail();
      if (dFail && !(keepOrders && keepOrders.length)) return dFail;
      var orders = r[0] ? normalizeOrders(r[0]) : keepOrders, uid = r[1];
      if (!orders.length) return new Fail('no-orders');
      if (!uid || !UUID.test(uid)) return new Fail('user-id');
      return Promise.all([M.skio(Q_SUB, { subscriptionId: id }, o), M.skio(Q_LIST, { storefrontUserId: uid }, o)]).then(function (q) {
        var a = q[0], b = q[1];
        var listRaw = b && !b.errors && b.data && Array.isArray(b.data.Subscriptions) ? b.data.Subscriptions : null;
        var subs = listRaw ? listRaw.map(function (s) { return normSub(s, false); }).filter(Boolean) : null;
        if (subs && subs.length !== listRaw.length) subs = null;
        var raw = a && !a.errors && a.data ? a.data.SubscriptionByPk : null;
        var sub = raw && raw.id === id ? normSub(raw, true) : null;
        if (!sub && subs) sub = subs.filter(function (s) { return s.id === id; })[0] || null;
        if (!sub) return new Fail('sub-query');
        var siteId = raw && typeof raw.siteId === 'string' && UUID.test(raw.siteId) ? raw.siteId : SITE_ID;
        return M.skio(Q_LOYALTY, { storefrontUserId: uid, siteId: siteId }, { ttl: 300000 }).then(function (c) {
          return { orders: orders, sub: sub, subs: subs, loyalty: parseLoyalty(c), deliveryFailed: dFail ? dFail.reason : null };
        });
      });
    });
  }
  function loadHome(fresh) {
    return M.delivery(HOME_LIMIT, { fresh: !!fresh }).then(function (body) {
      return body ? { orders: normalizeOrders(body) } : deliveryFail();
    });
  }
  function loadOrders(fresh) {
    return M.delivery(ORDERS_LIMIT, { fresh: !!fresh }).then(function (body) {
      return body ? { orders: normalizeOrders(body) } : deliveryFail();
    });
  }
  function startOrders(r, tries) {
    var my = { seq: r.seq, status: 'loading', data: null, tries: tries || 0, reason: null, retryAt: 0 };
    S.orders = my;
    guarded(loadOrders, [!!tries]).then(function (data) {
      if (S.orders !== my) return;
      var why = !data ? 'error' : data instanceof Fail ? data.reason : data.orders.length ? null : 'no-orders';
      my.data = why ? null : data;
      my.status = why ? 'none' : 'ready';
      my.reason = why;
      if (why) {
        clearOrderLines();
        retryLater(my, why, function () { if (S.orders === my) startOrders(M.route(), my.tries + 1); });
      }
      schedule();
    });
  }
  function clearOrderLines() {
    S.ocIds.forEach(function (id) { M.unmount(id); });
    S.ocIds = [];
  }
  function reconcileOrders() {
    var d = S.orders;
    if (!d || d.status !== 'ready') return;
    var page = M.q('#skio-root [data-skio-name="orders-page"]');
    if (!page) return;
    var model = ordersPageModel({ orders: d.data.orders, contact: CTX.contact, now: Date.now() });
    var seen = [];
    M.qa('[data-skio-name="order-card"]', page).forEach(function (card) {
      var name = cardOrderName(card), m = name ? model[name] : null;
      var header = M.byName('order-card-header', card);
      if (!m || !header) return;
      var id = IDS.orderLine + name.slice(1);
      if (seen.indexOf(id) >= 0) return;          /* the same order twice: first card only */
      var line = Object.assign({ order: name }, m);
      if (M.mount({ id: id, anchor: header, where: 'after', module: 'delivery', sig: hash(JSON.stringify(line)),
        build: function () { return buildOrderLine(line); } })) seen.push(id);
    });
    S.ocIds.forEach(function (id) { if (seen.indexOf(id) < 0) M.unmount(id); });
    S.ocIds = seen;
  }
  /* tries: the retries already made for this read (retryLater; a header change starts again at 0). A fresh re-read of
     the same subscription on the same route keeps the card already drawn (prev.data) while it reads; if then only the
     delivery read fails, the earlier orders are kept and the card is drawn from Skio's fresh answer (r7, E1 + review
     fix); any other failure removes it */
  function startDetail(r, fresh, tries) {
    var prev = S.detail && S.detail.subId === r.subscriptionId ? S.detail : null;
    var same = prev && prev.seq === r.seq;
    var my = { subId: r.subscriptionId, seq: r.seq, status: 'loading', data: null, fp: prev ? prev.fp : null,
      tries: tries || 0, reason: null, retryAt: 0, pending: true };
    if (same && prev.data) { my.data = prev.data; my.status = 'ready'; }
    S.detail = my;
    function again() { if (S.detail === my) startDetail(M.route(), true, my.tries + 1); }
    guarded(loadDetail, [r.subscriptionId, fresh, same && prev.data ? prev.data.orders : null]).then(function (data) {
      if (S.detail !== my) return;
      my.pending = false;
      var why = !data ? 'error' : data instanceof Fail ? data.reason : null;
      if (!why) {
        my.data = data; my.status = 'ready'; my.reason = null;
        if (data.deliveryFailed) {
          /* only the delivery read failed: the orders already shown, Skio's fresh subscription; try the delivery again */
          M.log('delivery: re-read failed, orders kept', data.deliveryFailed);
          retryLater(my, data.deliveryFailed, again);
        }
      } else if (my.data && /^delivery-failed/.test(why)) {
        /* defensive (a re-read passes the earlier orders, so a delivery failure comes back as data above) */
        my.status = 'ready';
        M.log('delivery: re-read failed, card kept', why);
      } else {
        my.data = null;
        my.status = 'none';
        my.reason = why;
        M.unmount(IDS.timeline);
      }
      if (why) retryLater(my, why, again);
      schedule();
    });
  }
  function startHome(r, tries) {
    var my = { seq: r.seq, status: 'loading', data: null, tries: tries || 0, reason: null, retryAt: 0 };
    S.home = my;
    guarded(loadHome, [!!tries]).then(function (data) {
      if (S.home !== my) return;
      var why = !data ? 'error' : data instanceof Fail ? data.reason : null;
      my.data = why ? null : data;
      my.status = why ? 'none' : 'ready';
      my.reason = why;
      if (why) {
        M.unmount(IDS.banner);
        retryLater(my, why, function () { if (S.home === my) startHome(M.route(), my.tries + 1); });
      }
      schedule();
    });
  }
  function nativeFingerprint() {
    var h = M.byName('subscription-header');
    return h ? (h.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }
  function reconcileDetail(r) {
    var d = S.detail;
    if (!d || d.status !== 'ready' || d.subId !== r.subscriptionId) return;
    var grid = M.q('#skio-root [data-skio-name="subscription-details-main-grid"]');
    if (!grid || !M.q('[data-skio-name="products-card"]', grid)) { d.reason = 'grid-missing'; return; }   /* page not rendered yet */
    /* the customer changed something in Skio (skip, date, products): read fresh data once */
    var fp = nativeFingerprint();
    if (d.fp === null) d.fp = fp;
    else if (fp && fp !== d.fp && Date.now() - S.refreshAt > 3000) {
      d.fp = fp;
      S.refreshAt = Date.now();
      setTimeout(function () { if (S.detail === d) startDetail(M.route(), true); }, 800);
    }
    var why = {};
    var model = timelineModel({ orders: d.data.orders, sub: d.data.sub, subs: d.data.subs, loyalty: d.data.loyalty,
      badge: streakBadge(), contact: CTX.contact, now: Date.now() }, why);
    d.reason = model ? null : why.reason || 'no-model';
    if (!model) { M.unmount(IDS.timeline); return; }
    M.mount({ id: IDS.timeline, anchor: grid, where: 'prepend', module: 'delivery', sig: hash(JSON.stringify(model)),
      build: function () { return buildTimeline(model); } });
  }
  function reconcileHome() {
    var h = S.home;
    if (!h || h.status !== 'ready') return;
    var page = M.q('#skio-root [data-skio-name="subscriptions-page"]');
    if (!page || M.byName('subscriptions-loading')) return;
    var model = bannerModel({ orders: h.data.orders, contact: CTX.contact, now: Date.now() });
    h.reason = model ? null : 'nothing-to-show';
    if (!model) { M.unmount(IDS.banner); return; }
    var anchor = null, where = 'prepend';
    var slot = document.getElementById(IDS.slot);
    if (slot && slot.isConnected && !slot.hasAttribute('data-mk-hidden-by')) anchor = slot;
    else {
      var ready = M.byName('subscriptions-active-header', page) || M.byName('subscriptions-inactive-header', page) ||
        M.byName('subscriptions-no-subscriptions-container', page);
      if (!ready) return;
      var greet = M.byName('subscriptions-greeting', page);
      if (greet) { anchor = greet; where = 'after'; }
      else anchor = M.q(':scope > div', page);
    }
    if (!anchor) { M.unmount(IDS.banner); return; }
    M.mount({ id: IDS.banner, anchor: anchor, where: where, module: 'delivery', sig: hash(JSON.stringify(model)),
      build: function () { return buildBanner(model); } });
  }
  function closePop(reason) {
    try { if (P && P.h && P.h.isOpen()) P.h.close(reason, { returnFocus: false }); } catch (e) { /* ignore */ }
    P = null;
  }
  function reconcile() {
    S.timer = null;
    try {
      if (!M.on()) { M.unmount(IDS.timeline); M.unmount(IDS.banner); clearOrderLines(); closePop('off'); return; }
      if (CTX.contact === null) CTX.contact = contactUrl() || '';
      var r = M.route();
      if (r.page === 'subscription') reconcileDetail(r); else M.unmount(IDS.timeline);
      if (r.page === 'subscriptions') reconcileHome(); else M.unmount(IDS.banner);
      if (r.page === 'orders') reconcileOrders(); else clearOrderLines();
    } catch (e) {
      M.log('delivery: hidden after an error', e && e.message);
      M.unmount(IDS.timeline);
      M.unmount(IDS.banner);
      clearOrderLines();
    }
  }
  function schedule() {
    if (S.timer) return;
    S.timer = setTimeout(reconcile, 100);
  }
  function watch(onPage) {
    try {
      if (onPage && !S.obs) {
        S.obs = new MutationObserver(schedule);
        S.obs.observe(document.body || document.documentElement, { childList: true, subtree: true, characterData: true });
      } else if (!onPage && S.obs) { S.obs.disconnect(); S.obs = null; }
    } catch (e) { /* ignore: route changes still reconcile */ }
  }
  function onRoute(r) {
    try {
      if (r.page === 'subscription' && r.subscriptionId) {
        if (!S.detail || S.detail.subId !== r.subscriptionId || S.detail.seq !== r.seq) startDetail(r, false);
      } else { S.detail = null; M.unmount(IDS.timeline); }
      if (r.page === 'subscriptions') {
        if (!S.home || S.home.seq !== r.seq) startHome(r);
      } else { S.home = null; M.unmount(IDS.banner); }
      if (r.page === 'orders') {
        if (!S.orders || S.orders.seq !== r.seq) startOrders(r);
      } else { S.orders = null; clearOrderLines(); }
      watch(r.page === 'subscription' || r.page === 'subscriptions' || r.page === 'orders');
      schedule();
    } catch (e) { M.unmount(IDS.timeline); M.unmount(IDS.banner); clearOrderLines(); }
  }

  M.onRoute(onRoute);

  window.MKPortalDelivery = Object.freeze({
    version: VERSION,
    /* re-read everything (fresh from the network) and redraw */
    refresh: function () {
      try {
        var r = M.route();
        if (r.page === 'subscription' && r.subscriptionId) startDetail(r, true);
        if (r.page === 'subscriptions') { S.home = null; M.invalidate('delivery'); startHome(r); }
        if (r.page === 'orders') { S.orders = null; M.invalidate('delivery'); startOrders(r); }
      } catch (e) { /* ignore */ }
    },
    /* r7 (E1): why each card is shown or not, for tests and the live checks (codes and counts only, no personal data).
       timeline / banner: 'shown' | 'loading' (a read or a retry pending) | 'none' | null (not this page);
       reason: null when shown, else 'delivery-failed:<429|503|timeout|network|no-token|bad-body|unknown>', 'user-id',
       'sub-query', 'error', 'no-orders', 'no-matching-order', 'no-headline', 'no-sub', 'grid-missing',
       'nothing-to-show' (banner: no recent order to tell about); retries: retries made for the current read (on this
       route; a change in Skio's header starts a new read at 0);
       lastStatus: the core's last /v1/orders code (MKPortal.deliveryStatus) */
    state: function () {
      try {
        var one = function (st, id) {
          if (!st) return { card: null, reason: null, retries: 0, retryPending: false };
          var shown = !!(document.getElementById(id) || {}).isConnected;
          var waiting = !!st.retryAt || !!st.pending || st.status === 'loading';
          return { card: shown ? 'shown' : waiting ? 'loading' : 'none', reason: shown ? null : st.reason || null,
            retries: st.tries || 0, retryPending: !!st.retryAt };
        };
        var t = one(S.detail, IDS.timeline), b = one(S.home, IDS.banner), o = S.orders;
        var ls = typeof M.deliveryStatus === 'function' ? M.deliveryStatus() : null;
        return { timeline: t.card, reason: t.reason, retries: t.retries, retryPending: t.retryPending,
          banner: b.card, bannerReason: b.reason, bannerRetries: b.retries,
          orderLines: o ? S.ocIds.length : null, ordersReason: o ? o.reason || null : null, ordersRetries: o ? o.tries || 0 : 0,
          lastStatus: ls ? ls.status : null };
      } catch (e) { return null; }
    },
    /* the Track package pop-up's state (tests and live checks: counts and words only, no personal data; noteRows: per
       package, the rows whose main line is the carrier's note, a count, never the text) */
    trackState: function () {
      try {
        if (!P || !P.h || !P.h.isOpen()) return null;
        return { order: P.m.order, fetch: P.fetch, tab: P.tab, packages: P.m.packs.length,
          events: P.m.packs.map(function (p) { return p.events.length; }), full: P.m.packs.map(function (p) { return p.full; }),
          noteRows: P.m.packs.map(function (p) { return p.events.filter(function (e) { return e.text !== e.words; }).length; }) };
      } catch (e) { return null; }
    },
    /* pure model functions, exposed for tests (plain data in and out) */
    models: Object.freeze({
      normalizeOrders: normalizeOrders,
      banner: bannerModel,
      ordersPage: ordersPageModel,
      timeline: timelineModel,
      normSub: normSub,
      parseLoyalty: parseLoyalty,
      track: popModel,
      trackApply: popApply,
      canTrack: canTrack,
      /* r6: a scan's note as shown (cleanNote, then the sentence case), or null */
      scanNote: function (v, city, province) { return noteCase(cleanNote(v), str(city, 80), str(province, 80)); }
    })
  });
})();
